<template>
  <AuthenticatedView
    :loading="settingsLoading && !settingsResult"
    :current-user="currentUserForHeader"
    :has-result="!!settingsResult"
  >
    <AppHeader
      v-if="currentUserForHeader"
      :user="currentUserForHeader"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div v-if="isValidBoardId" :class="['card', { 'full-width': isFullWidth }]">
      <breadcrumbs :items="breadcrumbsItems" :link-last-item="true" />
      <div
        class="board-header-row"
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        "
      >
        <div class="board-header-info">
          <h2
            class="board-header-title"
            style="margin: 0; display: inline-flex; align-items: center; gap: 8px"
          >
            <span>{{ board?.title || s__('Boards|Loading...') }}</span>
            <button
              v-if="board && !isMobileViewport"
              type="button"
              class="info-icon-button"
              @click.stop="showInfoModal = true"
              :title="s__('Boards|Board Information')"
              aria-label="Board Information"
            >
              <InfoIcon />
            </button>
          </h2>
          <div v-if="board?.description" class="muted" style="font-size: 13px; margin-top: 4px">
            {{ board.description }}
          </div>
        </div>
        <div v-if="!isMobileViewport" style="display: flex; gap: 8px; align-items: center">
          <button
            v-if="canToggleFullWidth"
            type="button"
            class="btn-link-secondary btn-sm board-header-action-btn btn-full-width-toggle"
            @click="toggleFullWidth"
            :title="
              isFullWidth ? s__('Boards|Exit full-width mode') : s__('Boards|Enter full-width mode')
            "
          >
            <span class="btn-text">{{ isFullWidth ? '⊟' : '⊞' }}</span>
          </button>
          <button
            v-if="board?.canManageBoard"
            type="button"
            class="btn-link-secondary btn-sm board-header-action-btn"
            @click="showColumnManagement = true"
          >
            <span class="btn-text">{{ s__('Boards|Manage Columns') }}</span>
            <span class="btn-icon board-header-action-icon">
              <SettingsIcon />
            </span>
          </button>
        </div>
      </div>
      <div
        v-if="isMobileViewport"
        ref="boardMenuRef"
        class="board-mobile-menu board-mobile-actions-line"
      >
        <div class="board-mobile-actions-row">
          <button
            type="button"
            class="btn-link-secondary board-mobile-controls-trigger"
            @click="openMobileControlsSheet"
          >
            {{ s__('Boards|Board controls') }}
          </button>
          <button
            type="button"
            class="btn-link-secondary board-mobile-menu-button"
            @click.stop="toggleBoardMenu"
          >
            {{ s__('Boards|Board menu') }}
          </button>
        </div>
        <div v-if="isBoardMenuOpen" class="board-mobile-menu-dropdown">
          <button type="button" class="board-mobile-menu-item" @click="openBoardInfoFromMenu">
            {{ s__('Boards|Board Information') }}
          </button>
          <button
            v-if="board?.canManageBoard"
            type="button"
            class="board-mobile-menu-item"
            @click="openColumnManagementFromMenu"
          >
            {{ s__('Boards|Manage Columns') }}
          </button>
        </div>
      </div>

      <div v-if="loading">{{ __('Loading…') }}</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="!board" class="muted">{{ s__('Boards|Board not found') }}</div>
      <div
        v-else-if="board?.isProjectBoard && board?.project && !board.project.boardsEnabled"
        class="task-info-container"
      >
        <div class="confidential-message">
          <div class="confidential-icon">📋</div>
          <h3>{{ s__('Boards|Boards Feature Disabled') }}</h3>
          <p>
            {{
              s__(
                'Boards|Boards feature is disabled for this project. It can be enabled in project settings.'
              )
            }}
          </p>
        </div>
      </div>
      <div
        v-else-if="board?.isPersonalBoard && currentUser && !currentUser.boardsEnabled"
        class="task-info-container"
      >
        <div class="confidential-message">
          <div class="confidential-icon">📋</div>
          <h3>{{ s__('Boards|Boards Feature Disabled') }}</h3>
          <p>
            {{
              s__('Boards|Boards feature is disabled. It can be enabled in your profile settings.')
            }}
          </p>
        </div>
      </div>
      <div v-else>
        <div
          v-if="isMobileViewport && showMobileControlsSheet"
          class="board-controls-overlay"
          @click="closeMobileControlsSheet"
        />
        <div
          class="board-controls-container"
          :class="{
            'mobile-sheet': isMobileViewport,
            open: showMobileControlsSheet,
          }"
          @click.stop
        >
          <div v-if="isMobileViewport" class="board-mobile-controls-header">
            <strong>{{ s__('Boards|Board controls') }}</strong>
            <button
              type="button"
              class="board-mobile-controls-close"
              @click="closeMobileControlsSheet"
            >
              ×
            </button>
          </div>
          <div class="board-quick-filter-group">
            <div class="board-quick-filter-options mobile-underlined-checklist">
              <label
                v-for="option in quickRoleOptions"
                :key="option.value"
                class="board-quick-filter-option mobile-underlined-checklist-item"
              >
                <input
                  type="checkbox"
                  :checked="isQuickRoleChecked(option.value)"
                  :disabled="isQuickRoleDisabled(option.value)"
                  @change="toggleQuickRole(option.value)"
                />
                <span>{{ option.label }}</span>
              </label>
            </div>
          </div>

          <div v-if="filterAllAccessible" class="board-quick-filter-hint">
            {{ s__('Dashboard|All accessible tasks are shown.') }}
          </div>
          <!-- Filters Panel -->
          <div
            ref="filterWrapperRef"
            style="margin-bottom: 16px"
            class="board-filters-wrapper"
            :class="{ expanded: isFilterPanelExpanded }"
          >
            <task-filter-panel
              ref="filterPanelRef"
              :members="members"
              :priorities="priorities"
              :workflow-statuses="availableWorkflowStatuses"
              :grouped-workflow-statuses="groupedWorkflowStatuses"
              :labels="projectLabels"
              :task-categories="taskCategories"
              :grouped-task-categories="groupedTaskCategories"
              :initial-filters="currentFiltersForPanel"
              :is-personal-project="board?.isPersonalBoard || false"
              :show-task-type-filter="true"
              :personal-labels-enabled="personalLabelsEnabled"
              :project-id="projectId"
              :hide-include-finished="true"
              :projects="board?.isPersonalBoard ? userProjects : []"
              @apply="handleFilterApply"
            />
          </div>
          <div class="board-advanced-controls-row">
            <!-- Grouping -->
            <SingleSelectSetting
              v-if="board"
              v-model="selectedSwimlaneMode"
              :label="s__('Dashboard|Grouping')"
              :options="swimlaneModeOptions"
              :error="swimlaneFormError"
              input-id="board-swimlane-mode-select"
            />
            <!-- Ordering -->
            <OrderingPanel
              v-model:orderBy="sessionOrderBy"
              v-model:orderDirection="sessionOrderDirection"
              :default-order-by="boardDefaultOrderBy"
              :default-order-direction="boardDefaultOrderDirection"
              :show-save-as-default="false"
              :reset-label="s__('Tasks|Reset Ordering')"
              :saving="savingOrdering"
              :error="orderingFormError"
              @change="handleOrderingChange"
              @reset="resetSessionOrdering"
            />
          </div>
          <div class="board-inline-actions board-inline-actions-settings">
            <button
              type="button"
              class="btn-secondary btn-sm"
              :disabled="savingBoardDefaults"
              @click="restoreSavedBoardSettings"
            >
              {{ s__('Dashboard|Restore saved settings') }}
            </button>
            <button
              v-if="board?.canManageBoard"
              type="button"
              class="btn-secondary btn-sm"
              :disabled="savingBoardDefaults"
              @click="saveBoardDefaults"
            >
              {{
                savingBoardDefaults ? s__('Dashboard|Saving...') : s__('Dashboard|Save as default')
              }}
            </button>
          </div>
        </div>
        <!-- Kanban Board -->
        <div
          v-if="
            !contentLoading &&
            board.boardColumns !== undefined &&
            (!board.boardColumns || board.boardColumns.length === 0)
          "
          class="muted"
          style="margin-top: 16px"
        >
          {{ s__('Boards|No columns configured for this board') }}
        </div>
        <div
          v-if="
            isMobileViewport &&
            board.boardColumns &&
            board.boardColumns.length > 1 &&
            !contentLoading
          "
          class="board-mobile-column-pager"
        >
          <button
            v-for="column in board.boardColumns"
            :key="`pager-${column.id}`"
            type="button"
            class="board-mobile-column-chip"
            :class="{ active: String(activeMobileColumnId) === String(column.id) }"
            @click="scrollToColumn(column.id)"
          >
            <span class="board-mobile-column-chip-title">{{ column.title }}</span>
            <span v-if="column.tasksCount !== undefined" class="board-mobile-column-chip-count">
              {{ column.tasksCount }}
            </span>
          </button>
        </div>
        <div
          v-if="board.boardColumns && board.boardColumns.length > 0"
          ref="boardContainerRef"
          class="board-container"
          @scroll="handleBoardScroll"
          style="overflow-x: auto; margin-top: 16px"
        >
          <template v-if="getEffectiveSwimlaneMode() !== 'none'">
            <!-- Swimlanes View with Horizontal Alignment -->
            <div
              class="board-swimlanes-wrapper"
              style="display: flex; flex-direction: column; gap: 0"
            >
              <!-- Column Headers Row -->
              <div
                class="board-column-headers"
                style="display: flex; gap: 16px; margin-bottom: 16px"
              >
                <!-- Empty space for swimlane headers column -->
                <div style="min-width: 50px; max-width: 50px; flex-shrink: 0"></div>
                <!-- Column headers -->
                <div
                  v-for="column in board.boardColumns"
                  :key="`header-${column.id}`"
                  class="column-header board-column-header-cell"
                  :data-column-id="column.id"
                  :style="{
                    minWidth: '280px',
                    maxWidth: '280px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    borderBottom: '2px solid #e0e0e0',
                  }"
                >
                  <div style="display: flex; align-items: center; gap: 8px">
                    <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #333">
                      {{ column.title }}
                    </h3>
                    <span
                      v-if="column.tasksCount !== undefined"
                      class="task-count-badge"
                      style="
                        background: #fff;
                        border: 1px solid #ddd;
                        border-radius: 12px;
                        padding: 2px 8px;
                        font-size: 12px;
                        color: #666;
                      "
                    >
                      {{ column.tasksCount }}
                    </span>
                  </div>
                  <div
                    v-if="column.color"
                    class="column-color-indicator"
                    :style="{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: column.color,
                    }"
                  />
                </div>
              </div>

              <!-- Swimlane Rows -->
              <template v-for="swimlane in alignedSwimlanes" :key="swimlane.key">
                <div
                  class="swimlane-row"
                  :data-swimlane-key="swimlane.key"
                  style="
                    display: flex;
                    flex-direction: row;
                    margin-bottom: 16px;
                    align-items: flex-start;
                  "
                >
                  <!-- Swimlane Header (fixed on left, sticky vertically) -->
                  <div
                    v-if="swimlane.label"
                    class="swimlane-header"
                    style="
                      font-size: 16px;
                      font-weight: 600;
                      color: #fff;
                      padding: 12px 4px;
                      background: rgba(80, 170, 180, 0.7);
                      border-radius: 4px;
                      margin-right: 16px;
                      min-width: 50px;
                      max-width: 50px;
                      flex-shrink: 0;
                      position: sticky;
                      left: 0;
                      top: 0;
                      z-index: 10;
                      align-self: stretch;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      height: fit-content;
                      writing-mode: vertical-rl;
                      text-orientation: mixed;
                      transform: rotate(180deg);
                    "
                  >
                    {{ swimlane.label }}
                  </div>

                  <!-- Swimlane Content: Columns with equal heights -->
                  <div
                    class="swimlane-columns"
                    style="
                      display: flex;
                      gap: 16px;
                      align-items: stretch;
                      flex: 0 0 auto;
                      min-width: min-content;
                    "
                  >
                    <div
                      v-for="column in board.boardColumns"
                      :key="`${swimlane.key}-${column.id}`"
                      class="swimlane-column-cell"
                      :data-column-id="column.id"
                      :style="{
                        minWidth: '280px',
                        maxWidth: '280px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                      }"
                    >
                      <!-- Always render ul for SortableJS, even when empty -->
                      <ul
                        style="list-style: none; padding: 0; margin: 0; flex: 1; min-height: 120px"
                        :data-sortable-list="`swimlane-${swimlane.key}-column-${column.id}`"
                      >
                        <template
                          v-if="
                            !swimlane.tasksByColumn[column.id] ||
                            swimlane.tasksByColumn[column.id].length === 0
                          "
                        >
                          <li
                            class="muted sortable-placeholder"
                            data-sortable-placeholder="true"
                            style="
                              text-align: center;
                              padding: 16px;
                              font-size: 13px;
                              list-style: none;
                            "
                          >
                            {{ s__('Boards|No tasks') }}
                          </li>
                        </template>
                        <template v-else>
                          <li
                            v-for="task in swimlane.tasksByColumn[column.id]"
                            :key="task.id"
                            class="board-task-card"
                            :class="{ 'archived-task': task.archived }"
                            :data-task-id="task.id"
                            style="
                              background: #fff;
                              border: 1px solid #e0e0e0;
                              border-radius: 6px;
                              padding: 12px;
                              margin-bottom: 8px;
                              cursor: pointer;
                              transition: box-shadow 0.2s;
                            "
                            @click="goToTask(task, $event)"
                          >
                            <div class="task-card-header">
                              <div
                                class="task-title"
                                style="font-weight: 500; margin-bottom: 6px; font-size: 14px"
                              >
                                {{ task.title }}
                              </div>
                              <button
                                v-if="!isMobileViewport"
                                type="button"
                                class="task-drag-handle"
                                :title="s__('Boards|Drag task')"
                                :aria-label="s__('Boards|Drag task')"
                                @click.stop
                                @touchstart.stop
                              >
                                <span aria-hidden="true">⋮⋮</span>
                              </button>
                              <button
                                v-else-if="task.workflowStatus && !task.archived"
                                type="button"
                                class="task-transition-handle"
                                :title="s__('TaskRow|Change status')"
                                :aria-label="s__('TaskRow|Change status')"
                                :disabled="
                                  executingTransition ||
                                  (loadingTaskTransitions &&
                                    activeTransitionTaskId === String(task.id))
                                "
                                @click.stop="openTransitionSelectionFromMobile(task, column.id)"
                                @touchstart.stop
                              >
                                <span
                                  v-if="
                                    loadingTaskTransitions &&
                                    activeTransitionTaskId === String(task.id)
                                  "
                                  class="loading-dots"
                                  aria-hidden="true"
                                >
                                  ⋯
                                </span>
                                <TransitionIcon v-else />
                              </button>
                            </div>
                            <div
                              v-if="
                                (task.labels && task.labels.length > 0) ||
                                (task.personalLabels && task.personalLabels.length > 0) ||
                                task.archived
                              "
                              class="task-labels"
                              style="display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 6px"
                            >
                              <span v-if="task.archived" class="label-badge archived-label">
                                {{ s__('TaskRow|Archived') }}
                              </span>
                              <span
                                v-for="label in task.labels"
                                :key="label.id"
                                class="label-badge"
                                :style="{
                                  backgroundColor: label.color,
                                  color: '#fff',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                }"
                              >
                                {{ label.title }}
                              </span>
                              <span
                                v-for="personalLabel in task.personalLabels"
                                :key="'personal-' + personalLabel.id"
                                class="label-badge personal-label-badge"
                                :style="{
                                  backgroundColor: personalLabel.color,
                                  color: '#fff',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  border: '1px dashed rgba(255, 255, 255, 0.5)',
                                }"
                              >
                                {{ personalLabel.title }}
                              </span>
                            </div>
                            <div class="task-meta" style="font-size: 11px; color: #666">
                              <div v-if="task.assignee" class="task-meta-item task-meta-assignee">
                                {{ s__('TaskRow|Assignee:') }} {{ person(task.assignee) }}
                                <span
                                  v-if="getTaskProjectRole(task, 'assignee')"
                                  class="task-meta-project-role"
                                >
                                  ({{ getTaskProjectRole(task, 'assignee') }})
                                </span>
                              </div>
                              <div v-if="task.author" class="task-meta-item task-meta-author">
                                {{ s__('TaskRow|Author:') }} {{ person(task.author) }}
                                <span
                                  v-if="getTaskProjectRole(task, 'author')"
                                  class="task-meta-project-role"
                                >
                                  ({{ getTaskProjectRole(task, 'author') }})
                                </span>
                              </div>
                              <div
                                v-if="task.project && !board?.isProjectBoard"
                                class="task-meta-item task-meta-project"
                              >
                                {{ s__('TaskRow|Project:') }} {{ task.project.title }}
                              </div>
                              <div v-if="task.dueAt" class="task-meta-item task-meta-due">
                                {{ s__('TaskRow|Due:') }}
                                <formatted-date-time
                                  :value="task.dueAt"
                                  :locale="currentLocale"
                                  :timezone="currentTimezone"
                                />
                              </div>
                              <div v-if="task.finishedAt" class="task-meta-item task-meta-finished">
                                {{ s__('TaskRow|Finished:') }}
                                <formatted-date-time
                                  :value="task.finishedAt"
                                  :locale="currentLocale"
                                  :timezone="currentTimezone"
                                />
                              </div>
                              <div
                                v-if="task.priority"
                                class="task-meta-item task-meta-priority"
                                style="display: flex; align-items: center; gap: 4px"
                              >
                                <PriorityIcon :priority="task.priority" :size="14" />
                                <span>{{ translatePriority(task.priority) }}</span>
                              </div>
                              <div
                                v-if="task.workflowStatus"
                                class="task-meta-item task-meta-status status-indicator"
                              >
                                <span
                                  class="status-dot"
                                  :style="{
                                    backgroundColor: task.workflowStatus.color || '#6B7280',
                                  }"
                                ></span>
                                <span class="status-name">{{ task.workflowStatus.name }}</span>
                              </div>
                            </div>
                          </li>
                        </template>
                      </ul>
                      <!-- Load More Button for Swimlane Column -->
                      <div
                        v-if="
                          swimlane.hasMoreByColumn[column.id] &&
                          (swimlane.tasksByColumn[column.id]?.length || 0) > 0
                        "
                        style="margin-top: 8px; text-align: center"
                      >
                        <button
                          type="button"
                          class="btn-load-more-swimlane-tasks"
                          :disabled="swimlanePagination[`${swimlane.key}-${column.id}`]?.loading"
                          @click="loadMoreSwimlaneTasks(swimlane.key, column.id)"
                          style="
                            padding: 8px 16px;
                            background-color: #f5f5f5;
                            color: #666;
                            border: 1px solid #ddd;
                            border-radius: 4px;
                            font-size: 13px;
                            font-weight: 500;
                            cursor: pointer;
                            transition: background-color 0.2s;
                            width: 100%;
                          "
                        >
                          <span v-if="swimlanePagination[`${swimlane.key}-${column.id}`]?.loading">
                            {{ s__('Boards|Loading...') }}
                          </span>
                          <span v-else>
                            {{ s__('Boards|Load More') }}
                            <span
                              v-if="swimlane.totalCountByColumn[column.id]"
                              style="color: #999; font-weight: normal"
                            >
                              ({{ swimlane.tasksByColumn[column.id]?.length || 0 }} /
                              {{ swimlane.totalCountByColumn[column.id] }})
                            </span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template v-else>
            <!-- Regular View (No Swimlanes) -->
            <div class="board-columns" style="display: flex; gap: 16px; min-width: min-content">
              <div
                v-for="column in board.boardColumns"
                :key="column.id"
                class="board-column"
                :data-column-id="column.id"
                :style="{
                  minWidth: '280px',
                  maxWidth: '280px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  padding: '12px',
                }"
              >
                <!-- Column Header -->
                <div
                  class="column-header"
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #e0e0e0;
                  "
                >
                  <div style="display: flex; align-items: center; gap: 8px">
                    <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #333">
                      {{ column.title }}
                    </h3>
                    <span
                      v-if="column.tasksCount !== undefined"
                      class="task-count-badge"
                      style="
                        background: #fff;
                        border: 1px solid #ddd;
                        border-radius: 12px;
                        padding: 2px 8px;
                        font-size: 12px;
                        color: #666;
                      "
                    >
                      {{ column.tasksCount }}
                    </span>
                  </div>
                  <div
                    v-if="column.color"
                    class="column-color-indicator"
                    :style="{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: column.color,
                    }"
                  />
                </div>

                <!-- Column Tasks -->
                <div class="column-tasks" style="min-height: 120px">
                  <!-- Regular View (No Swimlanes) -->
                  <!-- Always render ul for SortableJS, even when empty -->
                  <ul
                    style="list-style: none; padding: 0; margin: 0; min-height: 120px"
                    :data-sortable-list="`column-${column.id}`"
                  >
                    <template v-if="!columnTasks[column.id] || columnTasks[column.id].length === 0">
                      <li
                        class="muted sortable-placeholder"
                        data-sortable-placeholder="true"
                        style="text-align: center; padding: 16px; font-size: 13px; list-style: none"
                      >
                        {{ s__('Boards|No tasks') }}
                      </li>
                    </template>
                    <template v-else>
                      <li
                        v-for="task in columnTasks[column.id]"
                        :key="task.id"
                        class="board-task-card"
                        :class="{ 'archived-task': task.archived }"
                        :data-task-id="task.id"
                        style="
                          background: #fff;
                          border: 1px solid #e0e0e0;
                          border-radius: 6px;
                          padding: 12px;
                          margin-bottom: 8px;
                          cursor: pointer;
                          transition: box-shadow 0.2s;
                        "
                        @click="goToTask(task, $event)"
                      >
                        <div class="task-card-header">
                          <div
                            class="task-title"
                            style="font-weight: 500; margin-bottom: 6px; font-size: 14px"
                          >
                            {{ task.title }}
                          </div>
                          <button
                            v-if="!isMobileViewport"
                            type="button"
                            class="task-drag-handle"
                            :title="s__('Boards|Drag task')"
                            :aria-label="s__('Boards|Drag task')"
                            @click.stop
                            @touchstart.stop
                          >
                            <span aria-hidden="true">⋮⋮</span>
                          </button>
                          <button
                            v-else-if="task.workflowStatus && !task.archived"
                            type="button"
                            class="task-transition-handle"
                            :title="s__('TaskRow|Change status')"
                            :aria-label="s__('TaskRow|Change status')"
                            :disabled="
                              executingTransition ||
                              (loadingTaskTransitions && activeTransitionTaskId === String(task.id))
                            "
                            @click.stop="openTransitionSelectionFromMobile(task, column.id)"
                            @touchstart.stop
                          >
                            <span
                              v-if="
                                loadingTaskTransitions && activeTransitionTaskId === String(task.id)
                              "
                              class="loading-dots"
                              aria-hidden="true"
                            >
                              ⋯
                            </span>
                            <TransitionIcon v-else />
                          </button>
                        </div>
                        <div
                          v-if="
                            (task.labels && task.labels.length > 0) ||
                            (task.personalLabels && task.personalLabels.length > 0) ||
                            task.archived
                          "
                          class="task-labels"
                          style="display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 6px"
                        >
                          <span v-if="task.archived" class="label-badge archived-label">
                            {{ s__('TaskRow|Archived') }}
                          </span>
                          <span
                            v-for="label in task.labels"
                            :key="label.id"
                            class="label-badge"
                            :style="{
                              backgroundColor: label.color,
                              color: '#fff',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }"
                          >
                            {{ label.title }}
                          </span>
                          <span
                            v-for="personalLabel in task.personalLabels"
                            :key="'personal-' + personalLabel.id"
                            class="label-badge personal-label-badge"
                            :style="{
                              backgroundColor: personalLabel.color,
                              color: '#fff',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: '1px dashed rgba(255, 255, 255, 0.5)',
                            }"
                          >
                            {{ personalLabel.title }}
                          </span>
                        </div>
                        <div class="task-meta" style="font-size: 11px; color: #666">
                          <div v-if="task.assignee" class="task-meta-item task-meta-assignee">
                            {{ s__('TaskRow|Assignee:') }} {{ person(task.assignee) }}
                            <span
                              v-if="getTaskProjectRole(task, 'assignee')"
                              class="task-meta-project-role"
                            >
                              ({{ getTaskProjectRole(task, 'assignee') }})
                            </span>
                          </div>
                          <div v-if="task.author" class="task-meta-item task-meta-author">
                            {{ s__('TaskRow|Author:') }} {{ person(task.author) }}
                            <span
                              v-if="getTaskProjectRole(task, 'author')"
                              class="task-meta-project-role"
                            >
                              ({{ getTaskProjectRole(task, 'author') }})
                            </span>
                          </div>
                          <div
                            v-if="task.project && !board?.isProjectBoard"
                            class="task-meta-item task-meta-project"
                          >
                            {{ s__('TaskRow|Project:') }} {{ task.project.title }}
                          </div>
                          <div v-if="task.dueAt" class="task-meta-item task-meta-due">
                            {{ s__('TaskRow|Due:') }}
                            <formatted-date-time
                              :value="task.dueAt"
                              :locale="currentLocale"
                              :timezone="currentTimezone"
                            />
                          </div>
                          <div v-if="task.finishedAt" class="task-meta-item task-meta-finished">
                            {{ s__('TaskRow|Finished:') }}
                            <formatted-date-time
                              :value="task.finishedAt"
                              :locale="currentLocale"
                              :timezone="currentTimezone"
                            />
                          </div>
                          <div
                            v-if="task.priority"
                            class="task-meta-item task-meta-priority"
                            style="display: flex; align-items: center; gap: 4px"
                          >
                            <PriorityIcon :priority="task.priority" :size="14" />
                            <span>{{ translatePriority(task.priority) }}</span>
                          </div>
                          <div
                            v-if="task.workflowStatus"
                            class="task-meta-item task-meta-status status-indicator"
                          >
                            <span
                              class="status-dot"
                              :style="{ backgroundColor: task.workflowStatus.color || '#6B7280' }"
                            ></span>
                            <span class="status-name">{{ task.workflowStatus.name }}</span>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ul>
                  <!-- Load More Button -->
                  <div
                    v-if="shouldShowLoadMore(column.id)"
                    style="margin-top: 8px; text-align: center"
                  >
                    <button
                      type="button"
                      class="btn-load-more-tasks"
                      :disabled="columnPagination[column.id]?.loading"
                      @click="loadMoreTasks(column.id)"
                      style="
                        padding: 8px 16px;
                        background-color: #f5f5f5;
                        color: #666;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        font-size: 13px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: background-color 0.2s;
                        width: 100%;
                      "
                    >
                      <span v-if="columnPagination[column.id]?.loading">
                        {{ s__('Boards|Loading...') }}
                      </span>
                      <span v-else>
                        {{ s__('Boards|Load More') }}
                        <span
                          v-if="columnPagination[column.id]?.totalCount"
                          style="color: #999; font-weight: normal"
                        >
                          ({{ columnTasks[column.id]?.length || 0 }} /
                          {{ columnPagination[column.id].totalCount }})
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Column Management Modal -->
      <div
        v-if="showColumnManagement"
        class="modal-overlay"
        style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        "
      >
        <div
          class="modal-content"
          style="
            background: #fff;
            border-radius: 8px;
            padding: 24px;
            max-width: 800px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
          "
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            "
          >
            <h3 style="margin: 0">{{ s__('Boards|Manage Columns') }}</h3>
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: none;
                padding: 4px 8px;
                font-size: 20px;
              "
              @click="closeColumnManagement"
            >
              ×
            </button>
          </div>

          <!-- Columns List -->
          <div style="margin-bottom: 24px">
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
              "
            >
              <h4 style="margin: 0">{{ s__('Boards|Columns') }}</h4>
              <button type="button" class="btn-sm btn-primary" @click="openCreateColumnModal">
                {{ s__('Boards|New Column') }}
              </button>
            </div>

            <div v-if="!board?.boardColumns || board.boardColumns.length === 0" class="muted">
              {{ s__('Boards|No columns yet') }}
            </div>
            <ul v-else style="list-style: none; padding: 0; margin: 0">
              <li
                v-for="column in board.boardColumns"
                :key="column.id"
                style="
                  border: 1px solid #e0e0e0;
                  border-radius: 6px;
                  padding: 12px;
                  margin-bottom: 8px;
                  background: #f9f9f9;
                "
              >
                <div style="display: flex; justify-content: space-between; align-items: start">
                  <div style="flex: 1">
                    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px">
                      <h5 style="margin: 0; font-size: 14px">{{ column.title }}</h5>
                      <span
                        v-if="column.color"
                        :style="{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: column.color,
                          display: 'inline-block',
                        }"
                      />
                    </div>
                    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px">
                      <span class="muted" style="font-size: 12px">
                        {{ s__('Boards|Position:') }} {{ column.position }}
                      </span>
                      <span class="muted" style="font-size: 12px">
                        {{ s__('Boards|Tasks:') }} {{ column.tasksCount || 0 }}
                      </span>
                    </div>
                    <div v-if="column.workflowStatuses?.length > 0" style="margin-top: 8px">
                      <div class="muted" style="font-size: 12px; margin-bottom: 4px">
                        {{ s__('Boards|Workflow Statuses:') }}
                      </div>
                      <div style="display: flex; gap: 4px; flex-wrap: wrap">
                        <span
                          v-for="status in column.workflowStatuses"
                          :key="status.id"
                          style="
                            background: #e0e0e0;
                            padding: 2px 8px;
                            border-radius: 4px;
                            font-size: 11px;
                          "
                        >
                          {{ status.name }}
                        </span>
                      </div>
                    </div>
                    <div v-else class="muted" style="font-size: 12px; margin-top: 4px">
                      {{ s__('Boards|No workflow statuses assigned') }}
                    </div>
                  </div>
                  <div style="display: flex; gap: 8px">
                    <button
                      type="button"
                      class="btn-xs btn-primary column-action-btn"
                      @click="editColumn(column)"
                    >
                      <span class="btn-text">{{ s__('Boards|Edit') }}</span>
                      <span><EditIcon :size="16" /></span>
                    </button>
                    <button
                      type="button"
                      class="btn-xs btn-danger column-action-btn"
                      @click="confirmDeleteColumn(column)"
                    >
                      <span class="btn-text">{{ s__('Boards|Delete') }}</span>
                      <span><TrashIcon :size="16" /></span>
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Create/Edit Column Modal -->
      <div
        v-if="showColumnModal"
        class="modal-overlay"
        style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
        "
      >
        <div
          class="modal-content"
          style="
            background: #fff;
            border-radius: 8px;
            padding: 24px;
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
          "
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            "
          >
            <h3 style="margin: 0">
              {{ editingColumn ? s__('Boards|Edit Column') : s__('Boards|New Column') }}
            </h3>
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: none;
                padding: 4px 8px;
                font-size: 20px;
                cursor: pointer;
              "
              @click="closeColumnModal"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <form @submit.prevent="handleColumnSubmit">
            <div class="row">
              <div style="flex: 1">
                <label> {{ s__('Boards|Title') }} * </label>
                <input v-model="columnForm.title" type="text" required />
              </div>
              <div style="flex: 0 0 150px">
                <label> {{ s__('Boards|Color') }} </label>
                <input
                  v-model="columnForm.color"
                  type="color"
                  :disabled="creatingColumn || updatingColumn"
                />
              </div>
            </div>
            <div class="row">
              <div>
                <label>
                  {{ s__('Boards|Position') }}
                </label>
                <input v-model.number="columnForm.position" type="number" min="0" />
              </div>
            </div>

            <!-- Workflow Statuses Assignment -->
            <div v-if="Object.keys(groupedWorkflowStatuses).length > 0" class="row">
              <div>
                <label>
                  {{ s__('Boards|Workflow Statuses') }}
                </label>
                <div
                  style="
                    max-height: 300px;
                    overflow-y: auto;
                    border: 1px solid #d0d7de;
                    border-radius: 8px;
                    padding: 12px;
                    background: #fff;
                  "
                >
                  <div
                    v-for="(group, key, index) in groupedWorkflowStatuses"
                    :key="key"
                    :style="{
                      marginBottom:
                        index === Object.keys(groupedWorkflowStatuses).length - 1 ? '0' : '16px',
                      paddingBottom:
                        index === Object.keys(groupedWorkflowStatuses).length - 1 ? '0' : '12px',
                      borderBottom:
                        index === Object.keys(groupedWorkflowStatuses).length - 1
                          ? 'none'
                          : '1px solid #e0e0e0',
                    }"
                  >
                    <div
                      v-if="group.project"
                      style="font-weight: 600; font-size: 13px; color: #666; margin-bottom: 4px"
                    >
                      {{ group.project.title }}
                    </div>
                    <div style="font-weight: 500; font-size: 13px; color: #333; margin-bottom: 6px">
                      {{ group.workflow.name }}
                    </div>
                    <div style="font-size: 12px; color: #666; margin-bottom: 8px">
                      {{ s__('Boards|Task Category:') }} {{ group.taskCategory.name }}
                    </div>
                    <div style="margin-left: 8px">
                      <label
                        v-for="status in group.statuses"
                        :key="status.id"
                        class="checkbox-label"
                        style="margin-bottom: 6px"
                      >
                        <input
                          v-model="columnForm.workflowStatusIds"
                          type="checkbox"
                          :value="status.id"
                        />
                        <span
                          :style="{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: status.color,
                            display: 'inline-block',
                            flexShrink: 0,
                          }"
                        />
                        <span>{{ status.name }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="muted" style="margin-bottom: 16px; font-size: 13px">
              {{ s__('Boards|No workflow statuses available') }}
            </div>

            <div v-if="columnFormError" class="error" style="margin-bottom: 16px; font-size: 13px">
              {{ columnFormError }}
            </div>
            <div style="display: flex; gap: 8px; justify-content: flex-end">
              <button
                type="button"
                class="btn-sm"
                style="
                  color: #666;
                  background: transparent;
                  border: 1px solid #ccc;
                  padding: 8px 16px;
                "
                @click="closeColumnModal"
              >
                {{ s__('Boards|Cancel') }}
              </button>
              <button
                type="submit"
                class="btn-sm"
                style="color: #fff; background: #1976d2; border: none; padding: 8px 16px"
                :disabled="creatingColumn || updatingColumn"
              >
                <span v-if="creatingColumn || updatingColumn">{{ s__('Boards|Saving...') }}</span>
                <span v-else>{{ s__('Boards|Save') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete Column Confirmation Modal -->
      <div
        v-if="deletingColumn"
        class="modal-overlay"
        style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1002;
        "
      >
        <div
          class="modal-content"
          style="background: #fff; border-radius: 8px; padding: 24px; max-width: 400px; width: 90%"
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            "
          >
            <h3 style="margin: 0">{{ s__('Boards|Delete Column') }}</h3>
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: none;
                padding: 4px 8px;
                font-size: 20px;
                cursor: pointer;
              "
              @click="deletingColumn = null"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p style="margin: 0 0 16px 0">
            {{
              s__(
                'Boards|Are you sure you want to delete this column? This action cannot be undone.'
              )
            }}
          </p>
          <div style="display: flex; gap: 8px; justify-content: flex-end">
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: 1px solid #ccc;
                padding: 8px 16px;
              "
              @click="deletingColumn = null"
            >
              {{ s__('Boards|Cancel') }}
            </button>
            <button
              type="button"
              class="btn-sm"
              style="color: #fff; background: #d32f2f; border: none; padding: 8px 16px"
              :disabled="deleting"
              @click="confirmDeleteColumnAction"
            >
              <span v-if="deleting">{{ s__('Boards|Deleting...') }}</span>
              <span v-else>{{ s__('Boards|Delete') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="card">
      <p>{{ s__('Boards|Board ID is required') }}</p>
    </div>

    <!-- Transition Modals -->
    <TransitionConfirmationModal
      :visible="showTransitionConfirmation"
      :transition="pendingTransition?.transitions?.[0]"
      :target-status="pendingTransition?.targetStatus"
      :task="pendingTransition?.task"
      :checklists-enabled="board?.project?.checklistsEnabled || false"
      :executing="executingTransition"
      @confirm="confirmTransition(pendingTransition?.transitions?.[0])"
      @cancel="cancelTransition"
    />

    <TransitionSelectionModal
      :visible="showTransitionSelection"
      :transitions="pendingTransition?.transitions || []"
      :task="pendingTransition?.task"
      :board-columns="board?.boardColumns || []"
      :checklists-enabled="board?.project?.checklistsEnabled || false"
      :executing="executingTransition"
      @confirm="transition => confirmTransition(transition)"
      @cancel="cancelTransition"
    />

    <!-- Info Modal -->
    <BoardShowInfoModal :visible="showInfoModal" @close="showInfoModal = false" />

    <!-- Toast Manager -->
    <ToastManager ref="toastManagerRef" />
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useQuery, useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, translatePriority } from '@/locale'
import { buildProjectRolesMap, getProjectRole } from '@/utils/projectRoles'
import Sortable from 'sortablejs'

// Make Sortable available globally for testing
if (typeof window !== 'undefined') {
  window.Sortable = Sortable

  // Suppress SortableJS lastElementChild errors
  // These errors occur during drag operations on empty lists but don't break functionality
  const originalErrorHandler = window.onerror
  window.onerror = function (message, source, lineno, colno, error) {
    // Suppress SortableJS lastElementChild errors
    if (
      message &&
      typeof message === 'string' &&
      message.includes('Cannot read properties of null') &&
      message.includes('lastElementChild')
    ) {
      // Check if it's related to SortableJS (check stack trace or source)
      const isSortableError =
        (error &&
          error.stack &&
          (error.stack.includes('sortable') ||
            error.stack.includes('_onDragOver') ||
            error.stack.includes('handleEvent'))) ||
        (source && source.includes('sortable'))

      if (isSortableError) {
        return true // Suppress the error
      }
    }
    // Call original error handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error)
    }
    return false
  }

  // Also catch unhandled promise rejections that might contain this error
  const unhandledRejectionHandler = function (event) {
    if (
      event.reason &&
      typeof event.reason === 'object' &&
      event.reason.message &&
      event.reason.message.includes('lastElementChild') &&
      event.reason.message.includes('Cannot read properties of null')
    ) {
      event.preventDefault() // Suppress the error
    }
  }
  window.addEventListener('unhandledrejection', unhandledRejectionHandler)
}
import Breadcrumbs from '../common/Breadcrumbs.vue'
import TaskFilterPanel from '../tasks/TaskFilterPanel.vue'
import FormattedDateTime from '../common/FormattedDateTime.vue'
import PriorityIcon from '../common/icons/PriorityIcon.vue'
import SettingsIcon from '../common/icons/SettingsIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import EditIcon from '../common/icons/EditIcon.vue'
import TrashIcon from '../common/icons/TrashIcon.vue'
import TransitionIcon from '../common/icons/TransitionIcon.vue'
import TransitionConfirmationModal from './TransitionConfirmationModal.vue'
import TransitionSelectionModal from './TransitionSelectionModal.vue'
import BoardShowInfoModal from './BoardShowInfoModal.vue'
import ToastManager from '../common/ToastManager.vue'
import OrderingPanel from '../common/OrderingPanel.vue'
import SingleSelectSetting from '../common/SingleSelectSetting.vue'
import AppHeader from '../common/AppHeader.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'
import '../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  boardId: { type: String, default: '' },
  projectId: String,
  chatId: String,
  fromId: String,
})

// GraphQL Query for Board Settings (just settings, no content)
const BOARD_SETTINGS_QUERY = gql`
  query BoardSettings($id: ID!) {
    board(id: $id) {
      id
      title
      description
      projectId
      userId
      isProjectBoard
      isPersonalBoard
      preFilters
      orderBy
      orderDirection
      swimlaneMode
      canManageBoard
      project {
        id
        title
        boardsEnabled
        labels {
          id
          title
          color
        }
        taskCategories {
          id
          name
        }
        members {
          id
          name
          username
        }
      }
    }
    currentUser {
      id
      name
      username
      personalLabelsEnabled
      preferredTimezone
      preferredLanguage
      boardsEnabled
    }
  }
`

// GraphQL Query for Board Content (with ordering and filters)
const BOARD_QUERY = gql`
  query Board(
    $id: ID!
    $sessionFilters: JSON
    $sessionOrderBy: String
    $sessionOrderDirection: String
    $sessionSwimlaneMode: String
  ) {
    board(
      id: $id
      sessionFilters: $sessionFilters
      sessionOrderBy: $sessionOrderBy
      sessionOrderDirection: $sessionOrderDirection
      sessionSwimlaneMode: $sessionSwimlaneMode
    ) {
      id
      title
      description
      projectId
      userId
      isProjectBoard
      isPersonalBoard
      preFilters
      orderBy
      orderDirection
      swimlaneMode
      canManageBoard
      boardColumns {
        id
        title
        position
        color
        tasksCount
        tasksPaginated(first: 20) {
          tasks {
            id
            iid
            title
            priority
            archived
            dueAt
            finishedAt
            assignee {
              id
              name
              username
            }
            author {
              id
              name
              username
            }
            workflowStatus {
              id
              name
              color
            }
            labels {
              id
              title
              color
            }
            personalLabels {
              id
              title
              color
            }
            project {
              id
              title
            }
            checklist {
              id
              completed
            }
            checklistCompleted
          }
          hasNextPage
          endCursor
          totalCount
        }
        swimlanes(firstPerSwimlane: 20) {
          key
          label
          hasMore
          totalCount
          tasks {
            id
            iid
            title
            priority
            archived
            dueAt
            finishedAt
            assignee {
              id
              name
              username
            }
            author {
              id
              name
              username
            }
            workflowStatus {
              id
              name
              color
            }
            labels {
              id
              title
              color
            }
            personalLabels {
              id
              title
              color
            }
            project {
              id
              title
            }
            checklist {
              id
              completed
            }
            checklistCompleted
          }
        }
        workflowStatuses {
          id
          name
        }
      }
      project {
        id
        title
        boardsEnabled
        labels {
          id
          title
          color
        }
        taskCategories {
          id
          name
        }
        members {
          id
          name
          username
        }
      }
    }
    currentUser {
      id
      name
      username
      personalLabelsEnabled
      preferredTimezone
      preferredLanguage
      boardsEnabled
    }
  }
`

// GraphQL Mutations
const CREATE_BOARD_COLUMN = gql`
  mutation CreateBoardColumn($boardId: ID!, $title: String!, $position: Int, $color: String) {
    createBoardColumn(boardId: $boardId, title: $title, position: $position, color: $color) {
      column {
        id
        title
        position
        color
        tasksCount
        workflowStatuses {
          id
          name
        }
      }
      errors
    }
  }
`

const UPDATE_BOARD_COLUMN = gql`
  mutation UpdateBoardColumn($id: ID!, $title: String, $position: Int, $color: String) {
    updateBoardColumn(id: $id, title: $title, position: $position, color: $color) {
      column {
        id
        title
        position
        color
        tasksCount
        workflowStatuses {
          id
          name
        }
      }
      errors
    }
  }
`

const DELETE_BOARD_COLUMN = gql`
  mutation DeleteBoardColumn($id: ID!) {
    deleteBoardColumn(id: $id) {
      column {
        id
      }
      errors
    }
  }
`

// Query to load more tasks for a column
// Note: This query fetches all columns, but we only use data for the target column
// Each column's tasksPaginated uses the same 'after' cursor, which is correct
// because each column maintains its own cursor in columnPagination state
const LOAD_MORE_COLUMN_TASKS = gql`
  query LoadMoreColumnTasks(
    $boardId: ID!
    $columnId: ID!
    $first: Int!
    $after: String
    $sessionFilters: JSON
    $sessionOrderBy: String
    $sessionOrderDirection: String
  ) {
    board(
      id: $boardId
      sessionFilters: $sessionFilters
      sessionOrderBy: $sessionOrderBy
      sessionOrderDirection: $sessionOrderDirection
    ) {
      id
      boardColumns(columnId: $columnId) {
        id
        tasksPaginated(first: $first, after: $after) {
          tasks {
            id
            iid
            title
            priority
            archived
            dueAt
            finishedAt
            assignee {
              id
              name
              username
            }
            author {
              id
              name
              username
            }
            workflowStatus {
              id
              name
              color
            }
            labels {
              id
              title
              color
            }
            personalLabels {
              id
              title
              color
            }
            project {
              id
              title
            }
          }
          hasNextPage
          endCursor
          totalCount
        }
      }
    }
  }
`

const LOAD_MORE_SWIMLANE_TASKS = gql`
  query LoadMoreSwimlaneTasks(
    $boardId: ID!
    $first: Int!
    $after: String
    $sessionFilters: JSON
    $sessionOrderBy: String
    $sessionOrderDirection: String
    $sessionSwimlaneMode: String
  ) {
    board(
      id: $boardId
      sessionFilters: $sessionFilters
      sessionOrderBy: $sessionOrderBy
      sessionOrderDirection: $sessionOrderDirection
      sessionSwimlaneMode: $sessionSwimlaneMode
    ) {
      id
      boardColumns {
        id
        swimlanes {
          key
          tasksPaginated(first: $first, after: $after) {
            tasks {
              id
              iid
              title
              priority
              archived
              dueAt
              finishedAt
              assignee {
                id
                name
                username
              }
              author {
                id
                name
                username
              }
              workflowStatus {
                id
                name
                color
              }
              labels {
                id
                title
                color
              }
              personalLabels {
                id
                title
                color
              }
              project {
                id
                title
              }
            }
            hasNextPage
            endCursor
            totalCount
          }
        }
      }
    }
  }
`

const UPDATE_COLUMN_WORKFLOW_STATUSES = gql`
  mutation UpdateBoardColumnWorkflowStatuses(
    $columnId: ID!
    $addWorkflowStatusIds: [ID!]
    $removeWorkflowStatusIds: [ID!]
  ) {
    updateBoardColumnWorkflowStatuses(
      columnId: $columnId
      addWorkflowStatusIds: $addWorkflowStatusIds
      removeWorkflowStatusIds: $removeWorkflowStatusIds
    ) {
      column {
        id
        workflowStatuses {
          id
          name
        }
      }
      errors
    }
  }
`

// Query for workflow statuses (for project board)
const PROJECT_WORKFLOW_STATUSES_QUERY = gql`
  query ProjectWorkflowStatuses($projectId: ID!) {
    project(id: $projectId) {
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            color
            position
          }
        }
      }
    }
  }
`

// Query for workflow statuses (for personal board - from all user projects)
const USER_WORKFLOW_STATUSES_QUERY = gql`
  query UserWorkflowStatuses {
    currentUser {
      id
      projects(first: 100) {
        edges {
          node {
            id
            title
            archived
            members {
              id
              name
              username
            }
            labels {
              id
              title
              color
            }
            taskCategories {
              id
              name
              workflow {
                id
                name
                statuses {
                  id
                  name
                  color
                  position
                }
              }
            }
          }
        }
      }
    }
  }
`

// Computed property to check if boardId is valid
const isValidBoardId = computed(() => {
  const boardId = props.boardId
  return boardId && typeof boardId === 'string' && boardId.trim() !== ''
})

// Track if component is mounted to prevent query before mount
const isMounted = ref(false)

// Full-width mode toggle
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)

function checkCanToggleFullWidth() {
  if (typeof window === 'undefined') return
  // Use requestAnimationFrame to ensure DOM is fully rendered
  requestAnimationFrame(() => {
    nextTick(() => {
      const cardElement = document.querySelector('.card')
      if (!cardElement) {
        // If element not found, try again after a short delay
        setTimeout(() => checkCanToggleFullWidth(), 100)
        return
      }

      const cardMaxWidth = parseInt(getComputedStyle(cardElement).maxWidth) || 900
      const screenWidth = window.innerWidth
      canToggleFullWidth.value = screenWidth > cardMaxWidth + 100 // Add some margin
      isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
    })
  })
}

function updateMobileViewport() {
  if (typeof window === 'undefined') return
  isMobileViewport.value = window.innerWidth <= 768
  if (!isMobileViewport.value) {
    isBoardMenuOpen.value = false
    showMobileControlsSheet.value = false
  }
}

function openMobileControlsSheet() {
  showMobileControlsSheet.value = true
}

function closeMobileControlsSheet() {
  showMobileControlsSheet.value = false
}

function handleGlobalKeydown(event) {
  if (event.key === 'Escape') {
    closeMobileControlsSheet()
  }
}

function toggleBoardMenu() {
  isBoardMenuOpen.value = !isBoardMenuOpen.value
}

function closeBoardMenu() {
  isBoardMenuOpen.value = false
}

function openBoardInfoFromMenu() {
  closeBoardMenu()
  showInfoModal.value = true
}

function openColumnManagementFromMenu() {
  closeBoardMenu()
  showColumnManagement.value = true
}

function handleBoardMenuOutsideClick(event) {
  if (!isBoardMenuOpen.value || !boardMenuRef.value) return
  if (!boardMenuRef.value.contains(event.target)) {
    closeBoardMenu()
  }
}

function getScrollableColumnElements() {
  const container = boardContainerRef.value
  if (!container) return []

  if (getEffectiveSwimlaneMode() !== 'none') {
    return Array.from(container.querySelectorAll('.board-column-header-cell[data-column-id]'))
  }

  return Array.from(container.querySelectorAll('.board-column[data-column-id]'))
}

function updateActiveMobileColumnFromScroll() {
  if (!isMobileViewport.value) return
  const container = boardContainerRef.value
  if (!container) return

  const columnElements = getScrollableColumnElements()
  if (columnElements.length === 0) return

  let nearestElement = columnElements[0]
  let nearestDistance = Math.abs(columnElements[0].offsetLeft - container.scrollLeft)

  for (const element of columnElements) {
    const distance = Math.abs(element.offsetLeft - container.scrollLeft)
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestElement = element
    }
  }

  const nearestColumnId = nearestElement.getAttribute('data-column-id')
  if (nearestColumnId) {
    activeMobileColumnId.value = nearestColumnId
  }
}

function ensureActiveMobileColumn() {
  const columnIds = board.value?.boardColumns?.map(column => String(column.id)) || []
  if (columnIds.length === 0) {
    activeMobileColumnId.value = null
    return
  }

  if (!activeMobileColumnId.value || !columnIds.includes(String(activeMobileColumnId.value))) {
    activeMobileColumnId.value = columnIds[0]
  }
}

function scrollToColumn(columnId) {
  const container = boardContainerRef.value
  if (!container) return

  const targetElement = container.querySelector(`[data-column-id="${columnId}"]`)
  if (!targetElement) return

  const containerRect = container.getBoundingClientRect()
  const targetRect = targetElement.getBoundingClientRect()
  const deltaLeft = targetRect.left - containerRect.left
  const targetLeft = Math.max(container.scrollLeft + deltaLeft - 8, 0)
  const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0)
  const clampedLeft = Math.min(targetLeft, maxScrollLeft)

  container.scrollTo({ left: clampedLeft, behavior: 'smooth' })
  activeMobileColumnId.value = String(columnId)
}

function toggleFullWidth() {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
}

function handleResize() {
  updateMobileViewport()
  checkCanToggleFullWidth()
  if (!canToggleFullWidth.value && isFullWidth.value) {
    isFullWidth.value = false
  }
}

// Helper function to restore session parameters from URL
function restoreSessionParamsFromUrl() {
  try {
    const urlParams = new URLSearchParams(window.location.search)

    // Restore session filters
    const sessionFiltersParam = urlParams.get('sessionFilters')
    if (sessionFiltersParam !== null) {
      if (sessionFiltersParam === '{}') {
        // Empty object = user explicitly wants no filters
        sessionFilters.value = {}
      } else {
        try {
          const parsed = JSON.parse(sessionFiltersParam)
          if (typeof parsed === 'object') {
            sessionFilters.value = parsed
          }
        } catch (e) {
          console.warn('Failed to parse sessionFilters from URL:', e)
        }
      }
    }

    // Restore session ordering
    const sessionOrderByParam = urlParams.get('sessionOrderBy')
    if (sessionOrderByParam !== null) {
      sessionOrderBy.value = sessionOrderByParam
    }

    const sessionOrderDirectionParam = urlParams.get('sessionOrderDirection')
    if (sessionOrderDirectionParam !== null) {
      sessionOrderDirection.value = sessionOrderDirectionParam
    }

    // Restore session swimlane mode
    const sessionSwimlaneModeParam = urlParams.get('sessionSwimlaneMode')
    if (sessionSwimlaneModeParam !== null) {
      sessionSwimlaneMode.value = sessionSwimlaneModeParam
    }
  } catch (e) {
    console.warn('Failed to restore session parameters from URL:', e)
  }
}

// Helper function to restore session parameters from sessionStorage
// This is called when URL doesn't have session parameters but sessionStorage does
function restoreSessionParamsFromStorage() {
  try {
    const savedUrl = sessionStorage.getItem(`boardUrl_${props.boardId}`)
    if (!savedUrl) return

    // Parse the saved URL to extract query parameters
    const url = new URL(savedUrl, window.location.origin)
    const urlParams = new URLSearchParams(url.search)

    let restoredAny = false

    // Only restore if URL doesn't already have these parameters (URL takes precedence)
    if (sessionFilters.value === null && urlParams.has('sessionFilters')) {
      const sessionFiltersParam = urlParams.get('sessionFilters')
      if (sessionFiltersParam === '{}') {
        sessionFilters.value = {}
        restoredAny = true
      } else {
        try {
          const parsed = JSON.parse(sessionFiltersParam)
          if (typeof parsed === 'object') {
            sessionFilters.value = parsed
            restoredAny = true
          }
        } catch (e) {
          console.warn('Failed to parse sessionFilters from sessionStorage:', e)
        }
      }
    }

    if (sessionOrderBy.value === '' && urlParams.has('sessionOrderBy')) {
      sessionOrderBy.value = urlParams.get('sessionOrderBy')
      restoredAny = true
    }

    if (sessionOrderDirection.value === '' && urlParams.has('sessionOrderDirection')) {
      sessionOrderDirection.value = urlParams.get('sessionOrderDirection')
      restoredAny = true
    }

    if (sessionSwimlaneMode.value === '' && urlParams.has('sessionSwimlaneMode')) {
      sessionSwimlaneMode.value = urlParams.get('sessionSwimlaneMode')
      restoredAny = true
    }

    // Update the URL to reflect the restored session parameters
    // This ensures the URL matches the restored state
    if (restoredAny) {
      const newUrl = new URL(window.location.href)
      // Add all session-related parameters from storage to the URL
      urlParams.forEach((value, key) => {
        if (key.startsWith('session')) {
          newUrl.searchParams.set(key, value)
        }
      })
      // Use replaceState to avoid adding to history
      window.history.replaceState({}, '', newUrl.toString())
    }
  } catch (e) {
    console.warn('Failed to restore session parameters from sessionStorage:', e)
  }
}

onMounted(() => {
  isMounted.value = true
  isFullWidth.value = readFullWidthPreference()
  updateMobileViewport()

  // Restore session parameters from URL if present (URL takes precedence)
  restoreSessionParamsFromUrl()

  // If URL doesn't have session parameters, try to restore from sessionStorage
  // This ensures session filters persist across page reloads
  restoreSessionParamsFromStorage()

  // Check if we can toggle full-width (for button visibility)
  // Use multiple nextTick/requestAnimationFrame to ensure DOM is ready
  nextTick(() => {
    requestAnimationFrame(() => {
      nextTick(() => {
        checkCanToggleFullWidth()
      })
    })
  })

  // Listen for resize events
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleBoardMenuOutsideClick)
  document.addEventListener('keydown', handleGlobalKeydown)

  // Initialize Sortable after board loads
  watch(
    () => board.value?.boardColumns,
    () => {
      nextTick(() => {
        if (board.value && board.value.boardColumns && board.value.boardColumns.length > 0) {
          initializeSortable()
        }
      })
    },
    { immediate: true }
  )
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleBoardMenuOutsideClick)
  document.removeEventListener('keydown', handleGlobalKeydown)
  // Clean up Sortable instances
  sortableInstances.value.forEach(instance => instance.destroy())
  sortableInstances.value = []
})

// Store board's default ordering values
const boardDefaultOrderBy = ref('created_at')
const boardDefaultOrderDirection = ref('asc')

// Current board query variables (for refetching)
const boardQueryVariables = ref({
  id: null,
  sessionFilters: null,
  sessionOrderBy: null,
  sessionOrderDirection: null,
})

// Step 1: Fetch board settings first (no content, no ordering dependencies)
const {
  result: settingsResult,
  loading: settingsLoading,
  error: settingsError,
} = useQuery(
  BOARD_SETTINGS_QUERY,
  () => ({
    id: props.boardId.trim(),
  }),
  {
    enabled: computed(() => isMounted.value && isValidBoardId.value),
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  }
)

// Step 2: Use query with enabled computed - only enable after settings are loaded
// We'll still call refetch() explicitly to control when it runs
const settingsReadyForContent = ref(false)
const shouldLoadBoard = ref(false) // Flag to prevent auto-loading when enabled

const {
  result,
  loading: contentLoading,
  error: contentError,
  refetch: refetchBoardContent,
} = useQuery(BOARD_QUERY, () => buildBoardQueryVariables(), {
  enabled: computed(
    () => settingsReadyForContent.value && isValidBoardId.value && shouldLoadBoard.value
  ),
  fetchPolicy: 'cache-first',
  nextFetchPolicy: 'cache-first',
  notifyOnNetworkStatusChange: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

// Combined loading state
const loading = computed(() => settingsLoading.value || contentLoading.value)
const error = computed(() => settingsError.value || contentError.value)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

// Watch for errors
watch(error, err => {
  if (err) {
    console.error('Board query error:', err)
  }
})

// Helper function to build board query variables from current state
function buildBoardQueryVariables() {
  // Determine session ordering values (only if different from board defaults)
  let sessionOrderByValue = null
  let sessionOrderDirectionValue = null

  if (sessionOrderBy.value && sessionOrderBy.value !== '') {
    if (sessionOrderBy.value !== boardDefaultOrderBy.value) {
      sessionOrderByValue = sessionOrderBy.value
    }
  }

  if (sessionOrderDirection.value && sessionOrderDirection.value !== '') {
    if (sessionOrderDirection.value !== boardDefaultOrderDirection.value) {
      sessionOrderDirectionValue = sessionOrderDirection.value
    }
  }

  // Handle sessionFilters:
  // - null = use pre-filters (default state, no session filters set)
  // - {} = user explicitly cleared all filters, don't apply any filters
  // - {...} = user set specific session filters
  let sessionFiltersValue = null
  if (sessionFilters.value !== null && sessionFilters.value !== undefined) {
    if (
      typeof sessionFilters.value === 'object' &&
      Object.keys(sessionFilters.value).length === 0
    ) {
      // Empty object = user explicitly wants no filters
      sessionFiltersValue = {}
    } else if (
      typeof sessionFilters.value === 'object' &&
      Object.keys(sessionFilters.value).length > 0
    ) {
      // Non-empty object = user set specific filters
      sessionFiltersValue = sessionFilters.value
    }
    // Otherwise null = use pre-filters
  }

  // Determine session swimlane mode (only if different from board default)
  let sessionSwimlaneModeValue = null
  if (sessionSwimlaneMode.value && sessionSwimlaneMode.value !== '') {
    const boardSwimlaneMode = board.value?.swimlaneMode || 'none'
    if (sessionSwimlaneMode.value !== boardSwimlaneMode) {
      sessionSwimlaneModeValue = sessionSwimlaneMode.value
    }
  }

  return {
    id: props.boardId.trim(),
    sessionFilters: sessionFiltersValue,
    sessionOrderBy: sessionOrderByValue,
    sessionOrderDirection: sessionOrderDirectionValue,
    sessionSwimlaneMode: sessionSwimlaneModeValue,
  }
}

// Track if refetch is in progress to prevent duplicate calls
let isRefetching = false

// Function to load board content explicitly
async function loadBoard() {
  if (!isValidBoardId.value || !settingsResult.value?.board) {
    return
  }

  if (isRefetching) {
    return
  }

  isRefetching = true
  try {
    const variables = buildBoardQueryVariables()
    boardQueryVariables.value = variables
    await refetchBoardContent(variables)
  } finally {
    isRefetching = false
  }
}

// Function to refetch board content with current variables
async function refetchBoard() {
  // Prevent concurrent refetches
  if (isRefetching) {
    return
  }

  isRefetching = true
  try {
    const variables = buildBoardQueryVariables()
    boardQueryVariables.value = variables
    await refetchBoardContent(variables)
  } finally {
    isRefetching = false
  }
}

// Fetch workflow statuses for project boards
const {
  result: projectWorkflowStatusesResult,
  // loading: projectWorkflowStatusesLoading, // Unused - lazy query only loads when explicitly called
  error: projectWorkflowStatusesError,
  load: loadProjectWorkflowStatusesQuery,
} = useLazyQuery(PROJECT_WORKFLOW_STATUSES_QUERY, () => {
  if (!workflowStatusesProjectId.value) {
    return undefined // Skip query if no projectId
  }
  return { projectId: String(workflowStatusesProjectId.value) }
})

// Fetch workflow statuses for personal boards
const {
  result: userWorkflowStatusesResult,
  // loading: userWorkflowStatusesLoading, // Unused - lazy query only loads when explicitly called
  load: loadUserWorkflowStatusesQuery,
} = useLazyQuery(USER_WORKFLOW_STATUSES_QUERY)

// GraphQL Mutation for updating board
const UPDATE_BOARD = gql`
  mutation UpdateBoard(
    $id: ID!
    $preFilters: JSON
    $orderBy: String
    $orderDirection: String
    $swimlaneMode: String
  ) {
    updateBoard(
      id: $id
      preFilters: $preFilters
      orderBy: $orderBy
      orderDirection: $orderDirection
      swimlaneMode: $swimlaneMode
    ) {
      board {
        id
        preFilters
        orderBy
        swimlaneMode
        orderDirection
      }
      errors
    }
  }
`

// Query for priorities
// Query for priorities - use camelCase field name as per GraphQL schema
const PRIORITIES_QUERY = gql`
  query Priorities {
    priorityOptions {
      value
      label
    }
  }
`

// Query for personal labels
const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
    }
  }
`

// Mutations
const { mutate: createColumnMutation } = useMutation(CREATE_BOARD_COLUMN)
const { mutate: updateColumnMutation } = useMutation(UPDATE_BOARD_COLUMN)
const { mutate: deleteColumnMutation } = useMutation(DELETE_BOARD_COLUMN)
const { mutate: updateColumnWorkflowStatusesMutation } = useMutation(
  UPDATE_COLUMN_WORKFLOW_STATUSES
)
const { mutate: updateBoardMutation } = useMutation(UPDATE_BOARD)

// GraphQL Query for available transitions
const GET_TASK_AVAILABLE_TRANSITIONS = gql`
  query GetTaskAvailableTransitions($projectId: ID!, $taskId: ID!, $boardColumnId: ID) {
    project(id: $projectId) {
      id
      checklistsEnabled
      task(id: $taskId) {
        id
        availableTransitions(boardColumnId: $boardColumnId) {
          id
          name
          targetStatus {
            id
            name
            color
            requiresCompletedChecklists
          }
        }
        checklist {
          id
          completed
        }
        checklistCompleted
      }
    }
  }
`

// GraphQL Mutation for executing transition
const EXECUTE_TRANSITION = gql`
  mutation ExecuteTransition($taskId: ID!, $transitionId: ID!) {
    executeTransition(taskId: $taskId, transitionId: $transitionId) {
      task {
        id
        workflowStatus {
          id
          name
          color
        }
        availableTransitions {
          id
          name
          targetStatus {
            id
            name
            color
          }
        }
      }
      errors
    }
  }
`

const { mutate: executeTransitionMutation } = useMutation(EXECUTE_TRANSITION)

// Variables ref for task transitions query
const taskTransitionsVars = ref({
  projectId: null,
  taskId: null,
  boardColumnId: null,
})

const {
  load: loadTaskTransitions,
  result: taskTransitionsResult,
  loading: loadingTaskTransitions,
  onResult: onTaskTransitionsResult,
} = useLazyQuery(
  GET_TASK_AVAILABLE_TRANSITIONS,
  () => {
    // Return undefined if required variables are missing
    if (!taskTransitionsVars.value.projectId || !taskTransitionsVars.value.taskId) {
      return undefined
    }
    return {
      projectId: taskTransitionsVars.value.projectId,
      taskId: taskTransitionsVars.value.taskId,
      boardColumnId: taskTransitionsVars.value.boardColumnId || null,
    }
  },
  {
    fetchPolicy: 'no-cache', // Use no-cache to prevent Apollo from merging and losing transitions
  }
)

// Store raw result to avoid cache normalization issues
const rawTaskTransitionsResult = ref(null)
let transitionsResultResolver = null

onTaskTransitionsResult(result => {
  // Deep clone to avoid Apollo cache normalization issues
  if (result.data?.project?.task?.availableTransitions) {
    rawTaskTransitionsResult.value = JSON.parse(JSON.stringify(result.data))
  } else {
    rawTaskTransitionsResult.value = result.data
  }

  // Resolve promise if waiting
  if (transitionsResultResolver) {
    transitionsResultResolver(result.data)
    transitionsResultResolver = null
  }
})

// Queries for filter data
const { result: prioritiesResult } = useQuery(PRIORITIES_QUERY)
const {
  result: personalLabelsResult,
  // loading: personalLabelsLoading, // Unused - lazy query only loads when explicitly called
  load: loadPersonalLabels,
} = useLazyQuery(PERSONAL_LABELS_QUERY)

// State
const board = ref(null)
const currentUser = ref(null)
const currentUserForHeader = computed(
  () => currentUser.value || settingsResult.value?.currentUser || null
)
const showColumnManagement = ref(false)
const showInfoModal = ref(false)
const boardMenuRef = ref(null)
const boardContainerRef = ref(null)
const isBoardMenuOpen = ref(false)
const isMobileViewport = ref(false)
const activeMobileColumnId = ref(null)
const showMobileControlsSheet = ref(false)
const showColumnModal = ref(false)
const editingColumn = ref(null)
const deletingColumn = ref(null)

// Lock body scroll when modals are open
useBodyScrollLock(showColumnManagement)
useBodyScrollLock(showColumnModal)
useBodyScrollLock(computed(() => !!deletingColumn.value))
useBodyScrollLock(showMobileControlsSheet)
const creatingColumn = ref(false)
const updatingColumn = ref(false)
const deleting = ref(false)
const columnFormError = ref('')
const filterFormError = ref('')
const availableWorkflowStatuses = ref([])
const groupedWorkflowStatuses = ref({})
const priorities = ref([])
const projectLabels = ref([])
const taskCategories = ref([])
const groupedTaskCategories = ref({})
const members = ref([])
const memberships = ref([])
const projectRolesMap = computed(() => buildProjectRolesMap(memberships.value))
const personalLabels = ref([])
const userProjects = ref([]) // Projects for personal boards filtering
const personalLabelsEnabled = ref(false)
const workflowStatusesProjectId = ref(null) // Store projectId for workflow statuses query
const currentTimezone = ref(null) // User's preferred timezone
const currentLocale = ref(null) // User's preferred locale
const filterPanelRef = ref(null) // Reference to TaskFilterPanel component
const filterWrapperRef = ref(null) // Reference to filter wrapper div
const isFilterPanelExpanded = ref(false) // Track if filter panel is expanded

// Column form
const columnForm = ref({
  title: '',
  color: '',
  position: 0,
  workflowStatusIds: [],
})

// Session filters (temporary, not saved to DB)
const sessionFilters = ref(null)
const filterAsAuthor = ref(true)
const filterAsAssignee = ref(true)
const filterAsSubscriber = ref(true)
const filterAllAccessible = ref(false)
const lastManualQuickRoles = ref({
  filterAsAuthor: true,
  filterAsAssignee: true,
  filterAsSubscriber: true,
})
const savingBoardDefaults = ref(false)

// Session ordering (temporary, not saved to DB)
// Initialize as empty - will be set from board's default values when board loads
const sessionOrderBy = ref('')
const sessionOrderDirection = ref('')
const savingOrdering = ref(false)
const orderingFormError = ref('')

// Swimlane mode (session-based, can override board default)
const sessionSwimlaneMode = ref('')
const swimlaneFormError = ref('')

// Drag-and-drop transitions
const toastManagerRef = ref(null)
const showTransitionConfirmation = ref(false)
const showTransitionSelection = ref(false)
const pendingTransition = ref(null) // { task, taskId, sourceColumnId, targetColumnId, transitions, targetStatus, dragEvent }
const executingTransition = ref(false)
const activeTransitionTaskId = ref(null)
const sortableInstances = ref([]) // Store Sortable instances for cleanup
const isDragging = ref(false) // Track if a drag operation is in progress
const lastDragEndedAt = ref(0) // Prevent accidental click right after drag

// Computed: Current filters for TaskFilterPanel (merged pre-filters + session filters)
const currentFiltersForPanel = computed(() => {
  const preFilters = board.value?.preFilters || {}

  // Handle session filters:
  // - null/undefined = use pre-filters (default state)
  // - {} = user explicitly cleared all filters, show empty filters
  // - {...} = user set specific filters, merge with pre-filters
  let session = {}
  if (sessionFilters.value === null || sessionFilters.value === undefined) {
    // No session filters set, use pre-filters
    session = preFilters
  } else if (
    typeof sessionFilters.value === 'object' &&
    Object.keys(sessionFilters.value).length === 0
  ) {
    // Empty object = user explicitly wants no filters
    session = {}
  } else {
    // Non-empty object = merge session filters with pre-filters
    session = { ...preFilters, ...sessionFilters.value }
  }

  // Return filters (session already contains the correct merged or empty state)
  return {
    search: session.search || '',
    // Multi-select filters (OR logic)
    authorIds: session.authorIds && session.authorIds.length > 0 ? session.authorIds : undefined,
    assigneeIds:
      session.assigneeIds && session.assigneeIds.length > 0 ? session.assigneeIds : undefined,
    priorities:
      session.priorities && session.priorities.length > 0 ? session.priorities : undefined,
    workflowStatusIds:
      session.workflowStatusIds && session.workflowStatusIds.length > 0
        ? session.workflowStatusIds
        : undefined,
    taskCategoryIds:
      session.taskCategoryIds && session.taskCategoryIds.length > 0
        ? session.taskCategoryIds
        : undefined,
    labelIds: session.labelIds && session.labelIds.length > 0 ? session.labelIds : [],
    personalLabelIds:
      session.personalLabelIds && session.personalLabelIds.length > 0
        ? session.personalLabelIds
        : [],
    projectIds: session.projectIds && session.projectIds.length > 0 ? session.projectIds : [],
    dueFrom: session.dueFrom && session.dueFrom.trim().length > 0 ? session.dueFrom : '',
    dueTo: session.dueTo && session.dueTo.trim().length > 0 ? session.dueTo : '',
    dueInDays: session.dueInDays || null,
    includeOverdue: session.includeOverdue !== undefined ? session.includeOverdue : true,
    // Note: includeFinished is not used in boards - users control this via workflow statuses in columns
    includeArchived: session.includeArchived !== undefined ? session.includeArchived : false,
    // Note: includeFinished is not used in boards - users control this via workflow statuses in columns
    // It's not included in currentFiltersForPanel
  }
})

const quickRoleOptions = computed(() => [
  { value: 'mine', label: s__('Dashboard|Mine') },
  { value: 'assigned', label: s__('Dashboard|Assigned') },
  { value: 'watching', label: s__('Dashboard|Watching') },
  { value: 'all', label: s__('Dashboard|All accessible') },
])

const selectedQuickRolesCount = computed(() => {
  let count = 0
  if (filterAsAuthor.value) count += 1
  if (filterAsAssignee.value) count += 1
  if (filterAsSubscriber.value) count += 1
  return count
})

function getMergedBoardFilters() {
  const preFilters = board.value?.preFilters || {}
  if (sessionFilters.value === null || sessionFilters.value === undefined) {
    return { ...preFilters }
  }
  if (typeof sessionFilters.value === 'object' && Object.keys(sessionFilters.value).length === 0) {
    return {}
  }
  return { ...preFilters, ...sessionFilters.value }
}

function syncQuickRolesFromMergedFilters() {
  const merged = getMergedBoardFilters()
  const allAccessible = merged.allAccessible === true
  filterAllAccessible.value = allAccessible

  if (allAccessible) {
    const manual = {
      filterAsAuthor: merged.asAuthor !== false,
      filterAsAssignee: merged.asAssignee !== false,
      filterAsSubscriber: merged.asSubscriber !== false,
    }
    if (!manual.filterAsAuthor && !manual.filterAsAssignee && !manual.filterAsSubscriber) {
      manual.filterAsAuthor = true
    }
    lastManualQuickRoles.value = manual
    filterAsAuthor.value = true
    filterAsAssignee.value = true
    filterAsSubscriber.value = true
    return
  }

  filterAsAuthor.value = merged.asAuthor !== false
  filterAsAssignee.value = merged.asAssignee !== false
  filterAsSubscriber.value = merged.asSubscriber !== false

  if (!filterAsAuthor.value && !filterAsAssignee.value && !filterAsSubscriber.value) {
    filterAsAuthor.value = true
  }

  lastManualQuickRoles.value = {
    filterAsAuthor: filterAsAuthor.value,
    filterAsAssignee: filterAsAssignee.value,
    filterAsSubscriber: filterAsSubscriber.value,
  }
}

function isQuickRoleChecked(roleName) {
  if (roleName === 'all') return filterAllAccessible.value
  if (roleName === 'mine') return filterAsAuthor.value
  if (roleName === 'assigned') return filterAsAssignee.value
  if (roleName === 'watching') return filterAsSubscriber.value
  return false
}

function isQuickRoleDisabled(roleName) {
  if (roleName === 'all') return false
  if (filterAllAccessible.value) return true

  const isChecked = isQuickRoleChecked(roleName)
  return isChecked && selectedQuickRolesCount.value === 1
}

// Computed
const breadcrumbsItems = computed(() => {
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''

  const items = [{ label: s__('Boards|Main'), url: `/dashboard${chat}${from}` }]
  if (board.value?.isProjectBoard && board.value?.project && props.projectId) {
    items.push({
      label: board.value.project.title,
      url: `/projects/${props.projectId}/dashboard${chat}${from}`,
    })
  }
  return items
})

// Pagination state per column
// exhausted: true means we've determined there are no more tasks (even if backend says otherwise)
const columnPagination = ref({}) // { columnId: { cursor: null, hasMore: false, loading: false, totalCount: 0, exhausted: false } }

// Store loaded tasks per column (can be extended with pagination)
const loadedColumnTasks = ref({}) // { columnId: [tasks] }

// Store loaded tasks per swimlane column (for pagination)
const loadedSwimlaneTasks = ref({}) // { "swimlaneKey-columnId": [tasks] }
const swimlanePagination = ref({}) // { "swimlaneKey-columnId": { cursor: null, hasMore: false, loading: false, totalCount: 0 } }

// Get tasks for a column (merge initial tasks with loaded tasks)
const columnTasks = computed(() => {
  const tasks = {}
  if (board.value?.boardColumns) {
    board.value.boardColumns.forEach(column => {
      const paginatedData = column.tasksPaginated

      // Get any additional loaded tasks
      const additionalTasks = loadedColumnTasks.value[column.id] || []

      // Use paginated data (always available now)
      if (paginatedData) {
        tasks[column.id] = [...(paginatedData.tasks || []), ...additionalTasks]
      } else {
        // Fallback if paginated data not available
        tasks[column.id] = additionalTasks
      }
    })
  }
  return tasks
})

// Computed property to check if "Load More" button should be shown for a column
const shouldShowLoadMore = columnId => {
  const pagination = columnPagination.value[columnId]
  if (!pagination) return false
  // Don't show if hasMore is false or column is exhausted
  return pagination.hasMore === true && pagination.exhausted !== true
}

function getTranslatedSwimlaneLabel(swimlaneMode, key, fallbackLabel) {
  const normalizedKey = String(key || '')

  if (swimlaneMode === 'by_due_date') {
    const dueDateLabels = {
      overdue: s__('Dashboard|Overdue'),
      today: s__('Dashboard|Due today'),
      upcoming: s__('Dashboard|Upcoming'),
      no_due_date: s__('Dashboard|No due date'),
      no_due: s__('Dashboard|No due date'),
      finished: s__('Dashboard|Finished'),
      archived: s__('TaskRow|Archived'),
    }
    return dueDateLabels[normalizedKey] || fallbackLabel
  }

  if (swimlaneMode === 'by_priority') {
    if (normalizedKey === 'no_priority' || normalizedKey === 'none') {
      return s__('TaskFilter|None')
    }

    const translatedPriority = translatePriority(normalizedKey)
    if (translatedPriority && translatedPriority !== normalizedKey.toUpperCase()) {
      return translatedPriority
    }
  }

  return fallbackLabel
}

// Get aligned swimlanes across all columns (for horizontal alignment)
const alignedSwimlanes = computed(() => {
  if (getEffectiveSwimlaneMode() === 'none' || !board.value?.boardColumns) {
    return []
  }

  // Collect all unique swimlane keys across all columns
  // Preserve order from the first column (server provides correct order)
  const allSwimlaneKeys = []
  const seenKeys = new Set()
  const swimlanesByColumn = {}

  board.value.boardColumns.forEach(column => {
    if (column.swimlanes) {
      swimlanesByColumn[column.id] = {}
      column.swimlanes.forEach(swimlane => {
        const key = swimlane.key || 'default'
        if (!seenKeys.has(key)) {
          allSwimlaneKeys.push(key)
          seenKeys.add(key)
        }
        swimlanesByColumn[column.id][key] = swimlane
      })
    }
  })

  // Create aligned swimlanes structure
  const aligned = []
  // Keep swimlane ordering stable for known grouping modes.
  let sortedKeys = allSwimlaneKeys
  const swimlaneMode = getEffectiveSwimlaneMode()
  if (swimlaneMode === 'by_due_date') {
    const dueOrder = ['overdue', 'today', 'upcoming', 'no_due_date', 'finished', 'archived']
    sortedKeys = [...allSwimlaneKeys].sort((a, b) => {
      const ai = dueOrder.indexOf(String(a))
      const bi = dueOrder.indexOf(String(b))
      const aRank = ai === -1 ? 999 : ai
      const bRank = bi === -1 ? 999 : bi
      if (aRank !== bRank) return aRank - bRank
      return String(a).localeCompare(String(b))
    })
  } else if (swimlaneMode === 'by_priority') {
    const priorityOrder = ['highest', 'high', 'medium', 'low', 'lowest', 'no_priority']
    sortedKeys = [...allSwimlaneKeys].sort((a, b) => {
      const ai = priorityOrder.indexOf(String(a))
      const bi = priorityOrder.indexOf(String(b))
      const aRank = ai === -1 ? 999 : ai
      const bRank = bi === -1 ? 999 : bi
      if (aRank !== bRank) return aRank - bRank
      return String(a).localeCompare(String(b))
    })
  }

  sortedKeys.forEach(key => {
    // Get label from first column that has this swimlane
    let label = null

    for (const column of board.value.boardColumns) {
      const swimlane = swimlanesByColumn[column.id]?.[key]
      if (swimlane && swimlane.label) {
        label = swimlane.label
        break
      }
    }

    // Collect tasks for this swimlane from each column (merge with loaded tasks)
    const tasksByColumn = {}
    const hasMoreByColumn = {}
    const totalCountByColumn = {}
    board.value.boardColumns.forEach(column => {
      const swimlane = swimlanesByColumn[column.id]?.[key]
      const initialTasks = swimlane?.tasks || []
      const loadedKey = `${key}-${column.id}`
      const loadedTasks = loadedSwimlaneTasks.value[loadedKey] || []
      // Merge initial tasks with loaded tasks
      tasksByColumn[column.id] = [...initialTasks, ...loadedTasks]
      // Check if there are more tasks (prioritize pagination state if it exists, as it's more accurate after loading)
      const pagination = swimlanePagination.value[loadedKey]
      if (pagination) {
        // Use pagination state if available (more accurate after loading more tasks)
        hasMoreByColumn[column.id] = pagination.hasMore || false
      } else {
        // Fall back to swimlane.hasMore for initial state
        hasMoreByColumn[column.id] = swimlane?.hasMore || false
      }
      // Get total count
      totalCountByColumn[column.id] =
        swimlane?.totalCount || pagination?.totalCount || tasksByColumn[column.id].length
    })

    // Only include swimlane if at least one column has tasks
    const hasAnyTasks = board.value.boardColumns.some(
      column => tasksByColumn[column.id] && tasksByColumn[column.id].length > 0
    )

    if (hasAnyTasks) {
      aligned.push({
        key: key,
        label: getTranslatedSwimlaneLabel(swimlaneMode, key, label),
        tasksByColumn: tasksByColumn,
        hasMoreByColumn: hasMoreByColumn,
        totalCountByColumn: totalCountByColumn,
      })
    }
  })

  return aligned
})

// Watch for filter panel expansion to show/hide our custom buttons
watch(
  () => filterPanelRef.value?.$el,
  el => {
    if (!el) return
    // Watch for changes in the filter panel's expanded state
    // TaskFilterPanel uses v-if="isExpanded", so filter-content exists only when expanded
    const observer = new MutationObserver(() => {
      const filterContent = el.querySelector('.filter-content')
      isFilterPanelExpanded.value = !!filterContent
    })
    observer.observe(el, { childList: true, subtree: true })
    // Initial check
    const filterContent = el.querySelector('.filter-content')
    isFilterPanelExpanded.value = !!filterContent
  },
  { immediate: true }
)

// Track if board has been loaded
let boardLoaded = false

// Watch board settings result - initialize and load board content
watch(
  settingsResult,
  async data => {
    if (!data?.board) return

    // Store board settings
    board.value = {
      ...data.board,
      project: data.board.project,
      boardColumns: [], // Will be populated when content loads
    }
    currentUser.value = data.currentUser

    // Store board defaults
    const boardOrderBy = data.board.orderBy || 'created_at'
    const boardOrderDirection = data.board.orderDirection || 'asc'
    const boardSwimlaneMode = data.board.swimlaneMode || 'none'

    boardDefaultOrderBy.value = boardOrderBy
    boardDefaultOrderDirection.value = boardOrderDirection

    // Initialize session values from board defaults (only if not already set from URL)
    // restoreSessionParamsFromUrl() was called in onMounted, so if values are still empty,
    // they weren't in the URL, so use board defaults
    if (sessionOrderBy.value === '') {
      // Keep empty - UI will show board defaults, but query will use null
    }
    if (sessionOrderDirection.value === '') {
      // Keep empty - UI will show board defaults, but query will use null
    }
    if (sessionSwimlaneMode.value === '') {
      sessionSwimlaneMode.value = boardSwimlaneMode
    }
    syncQuickRolesFromMergedFilters()

    // Enable content query - it will run automatically when enabled becomes true
    if (!boardLoaded) {
      boardLoaded = true
      settingsReadyForContent.value = true
      shouldLoadBoard.value = true
      // Query will run automatically now that enabled is true
    }
  },
  { immediate: true }
)

watch([sessionFilters, () => board.value?.preFilters], () => {
  syncQuickRolesFromMergedFilters()
})

// Watch board content result - update board data explicitly
watch(
  result,
  data => {
    if (!data?.board) return

    // Update board with content (merge with existing settings)
    Object.assign(board.value, {
      ...data.board,
      boardColumns: data.board.boardColumns || [],
    })

    // Reset loaded tasks when board data changes (filters applied, etc.)
    loadedColumnTasks.value = {}
    loadedSwimlaneTasks.value = {}

    // Update pagination state from board columns (don't reset, just update)
    // IMPORTANT: Preserve existing cursors for columns that have loaded more tasks
    // The initial query returns the first page, but columns may have loaded more
    const newPagination = {}
    if (data.board.boardColumns) {
      data.board.boardColumns.forEach(column => {
        const paginatedData = column.tasksPaginated
        const existingPagination = columnPagination.value[column.id]

        if (paginatedData) {
          // CRITICAL: Preserve existing cursor if column has loaded more tasks
          // The main board query always returns the first page (cursor from initial load),
          // so if a column has loaded more tasks (has a different cursor), we MUST preserve it
          const existingCursor = existingPagination?.cursor
          const queryCursor = paginatedData.endCursor
          const hasLoadedMore =
            existingCursor &&
            existingCursor !== null &&
            existingCursor !== '' &&
            existingCursor !== queryCursor

          // Update pagination state (preserve cursor and hasMore if column has loaded more)
          const currentLoading = existingPagination?.loading || false

          // Calculate total visible tasks (initial + loaded)
          const initialTasks = paginatedData.tasks || []
          const loadedTasks = loadedColumnTasks.value[column.id] || []
          const totalVisibleTasks = initialTasks.length + loadedTasks.length

          // Validate hasMore: if we have all tasks (visible >= totalCount), there are no more
          // CRITICAL: If column is exhausted (exhausted: true), always set hasMore to false
          // This prevents the main board query from resetting hasMore to true when we've already determined there are no more tasks
          let isExhausted = existingPagination?.exhausted === true
          let validatedHasMore
          if (isExhausted) {
            // Column is exhausted - always set hasMore to false
            validatedHasMore = false
          } else if (hasLoadedMore) {
            // Column has loaded more, preserve its hasMore state
            validatedHasMore = existingPagination.hasMore
          } else if (existingPagination && existingPagination.hasMore === false) {
            // Column hasn't loaded more, but hasMore was explicitly set to false
            // Preserve it to prevent resetting to true when backend incorrectly reports hasNextPage=true
            validatedHasMore = false
          } else {
            // Use backend's hasNextPage for initial state
            validatedHasMore = paginatedData.hasNextPage
          }

          // CRITICAL: If we have all tasks visible, there are no more to load
          // This handles cases where the backend incorrectly reports hasNextPage=true
          // when we've already loaded all tasks
          // NOTE: Only set exhausted if we've already determined it (from a previous loadMore attempt)
          // Don't set it here just because we have all tasks visible - the backend might be wrong about totalCount
          if (totalVisibleTasks >= paginatedData.totalCount && paginatedData.totalCount > 0) {
            // We have all tasks, so there are no more
            validatedHasMore = false
            // Only mark as exhausted if it was already exhausted (from a previous loadMore attempt)
            // Don't set it here - let the loadMore function set it when we actually try to load more
          }

          newPagination[column.id] = {
            // If column has loaded more, preserve its cursor; otherwise use query's cursor
            cursor: hasLoadedMore ? existingCursor : queryCursor,
            // Use validated hasMore (corrected if we have all tasks)
            hasMore: validatedHasMore,
            loading: currentLoading,
            // Always use the latest totalCount from the query
            totalCount: paginatedData.totalCount,
            // Preserve exhausted flag (if column is exhausted, keep it exhausted)
            exhausted: isExhausted || existingPagination?.exhausted || false,
          }
        } else {
          // Initialize pagination state if not exists
          // But preserve existing state if it exists (column may have loaded more)
          if (existingPagination) {
            newPagination[column.id] = {
              ...existingPagination,
              loading: false, // Clear loading state
              // Preserve exhausted flag
              exhausted: existingPagination.exhausted || false,
            }
          } else {
            const totalCount = column.tasksCount || 0
            newPagination[column.id] = {
              cursor: null,
              hasMore: false,
              loading: false,
              totalCount: totalCount,
              exhausted: false,
            }
          }
        }
      })
    }
    // Force reactivity update by creating a new object
    columnPagination.value = { ...newPagination }

    // Initialize swimlane pagination state from board columns
    const newSwimlanePagination = {}
    if (data.board.boardColumns) {
      data.board.boardColumns.forEach(column => {
        if (column.swimlanes) {
          column.swimlanes.forEach(swimlane => {
            const key = `${swimlane.key || 'default'}-${column.id}`
            // Check if pagination already exists (preserve loading state and cursor)
            const currentLoading = swimlanePagination.value[key]?.loading || false
            const currentCursor = swimlanePagination.value[key]?.cursor || null
            // Calculate initial cursor from last task if not already set
            let initialCursor = currentCursor
            if (!initialCursor && swimlane.tasks && swimlane.tasks.length > 0) {
              const lastTask = swimlane.tasks[swimlane.tasks.length - 1]
              if (lastTask?.id) {
                initialCursor = btoa(lastTask.id.toString())
              }
            }
            // Initialize pagination from swimlane data (hasMore and totalCount are available)
            newSwimlanePagination[key] = {
              cursor: initialCursor,
              hasMore: swimlane.hasMore || false,
              loading: currentLoading,
              totalCount: swimlane.totalCount || swimlane.tasks?.length || 0,
            }
          })
        }
      })
    }
    // Merge with existing pagination to preserve state from loaded tasks
    swimlanePagination.value = {
      ...swimlanePagination.value,
      ...newSwimlanePagination,
    }

    // Load filter-related data
    if (data.board.project) {
      projectLabels.value = data.board.project.labels || []
      taskCategories.value = data.board.project.taskCategories || []
      members.value = data.board.project.members || []
      memberships.value = data.board.project.memberships || []

      // Group task categories by project for project boards
      if (data.board.isProjectBoard && data.board.project.title) {
        groupedTaskCategories.value = {
          [data.board.project.title]: (data.board.project.taskCategories || []).map(cat => ({
            id: cat.id,
            name: cat.name,
          })),
        }
      }
    } else if (data.board.isPersonalBoard) {
      // For personal boards, load labels from all user projects via workflow statuses query
      // This will be populated when userWorkflowStatusesResult is loaded
      // Don't clear labels here - they might already be set by the watcher
      // If labels are already set (by the watcher running before board loads), keep them
      // Otherwise, they will be set when the query completes
      if (projectLabels.value.length === 0) {
        // Labels not set yet, will be set when query completes
        // No need to set to empty array explicitly
      }
    }

    // Set personal labels enabled from currentUser
    if (data?.currentUser) {
      currentUser.value = data.currentUser
      personalLabelsEnabled.value = data.currentUser.personalLabelsEnabled || false
      // Set timezone and locale from currentUser preferences
      if (data.currentUser.preferredTimezone) {
        currentTimezone.value = data.currentUser.preferredTimezone
      }
      if (data.currentUser.preferredLanguage) {
        currentLocale.value = data.currentUser.preferredLanguage === 'ru' ? 'ru' : 'en'
      }
    }

    // Load workflow statuses when board is loaded
    if (
      data.board.isProjectBoard &&
      (props.projectId || data.board.projectId || data.board.project?.id)
    ) {
      // Set projectId for workflow statuses query
      workflowStatusesProjectId.value =
        props.projectId || data.board.projectId || data.board.project?.id
      loadWorkflowStatuses()
    } else if (data.board.isPersonalBoard) {
      loadWorkflowStatuses()
    }

    // Load personal labels if enabled
    if (personalLabelsEnabled.value) {
      loadPersonalLabels()
    }
  },
  { flush: 'post' } // Run after all reactive updates to prevent Apollo from detecting changes
)

// Watch priorities result
watch(
  prioritiesResult,
  data => {
    // Apollo Client converts snake_case field names to camelCase in responses
    // So priority_options becomes priorityOptions in the response
    if (
      data?.priorityOptions &&
      Array.isArray(data.priorityOptions) &&
      data.priorityOptions.length > 0
    ) {
      priorities.value = data.priorityOptions
    } else if (
      data?.priority_options &&
      Array.isArray(data.priority_options) &&
      data.priority_options.length > 0
    ) {
      // Fallback: check snake_case directly (in case conversion doesn't happen)
      priorities.value = data.priority_options
    } else {
      // If no data yet, ensure we have an empty array
      priorities.value = []
    }
  },
  { immediate: true }
)

// Watch personal labels result
watch(
  personalLabelsResult,
  data => {
    if (data?.personalLabels) {
      personalLabels.value = data.personalLabels || []
    }
  },
  { immediate: true }
)

// Watch project workflow statuses result
watch(
  projectWorkflowStatusesResult,
  data => {
    if (data?.project?.taskCategories) {
      const grouped = {}
      const allStatuses = []

      data.project.taskCategories.forEach(category => {
        if (category.workflow?.statuses && category.workflow.statuses.length > 0) {
          // Group by task category ID (not workflow ID) to ensure each category gets its own group
          const categoryKey = `${category.id}`
          if (!grouped[categoryKey]) {
            grouped[categoryKey] = {
              workflow: {
                id: category.workflow.id,
                name: category.workflow.name,
              },
              taskCategory: {
                id: category.id,
                name: category.name,
              },
              statuses: [],
            }
          }
          category.workflow.statuses.forEach(status => {
            if (!grouped[categoryKey].statuses.find(s => s.id === status.id)) {
              grouped[categoryKey].statuses.push(status)
              if (!allStatuses.find(s => s.id === status.id)) {
                allStatuses.push(status)
              }
            }
          })
        }
      })

      // Sort statuses within each workflow
      Object.keys(grouped).forEach(key => {
        grouped[key].statuses.sort((a, b) => a.position - b.position)
      })

      groupedWorkflowStatuses.value = grouped
      availableWorkflowStatuses.value = allStatuses.sort((a, b) => a.position - b.position)

      // Group task categories by project (for project boards, it's a single project)
      if (data.project.title) {
        groupedTaskCategories.value = {
          [data.project.title]: data.project.taskCategories.map(cat => ({
            id: cat.id,
            name: cat.name,
          })),
        }
      }
    }
  },
  { immediate: true }
)

// Watch user workflow statuses result (for personal boards)
watch(
  userWorkflowStatusesResult,
  data => {
    if (data?.currentUser?.projects?.edges) {
      const grouped = {}
      const allStatuses = []
      const taskCategoriesByProject = {}
      const allLabels = [] // Collect labels from all projects
      const membersById = new Map() // Deduplicated members across all projects

      const projectsList = [] // Collect projects for filtering
      data.currentUser.projects.edges.forEach(edge => {
        const project = edge.node
        // Skip archived projects
        if (project.archived) {
          return
        }
        // Collect projects for filtering (only id and title needed)
        projectsList.push({
          id: project.id,
          title: project.title,
        })
        // Collect members from all non-archived projects with deduplication
        if (project.members && project.members.length > 0) {
          project.members.forEach(member => {
            if (!member || member.id == null) return
            const key = String(member.id)
            if (!membersById.has(key)) {
              membersById.set(key, member)
            }
          })
        }
        // Collect labels from all non-archived projects
        if (project.labels && project.labels.length > 0) {
          project.labels.forEach(label => {
            // Avoid duplicates by checking if label with same id already exists
            if (!allLabels.find(l => l.id === label.id)) {
              allLabels.push(label)
            }
          })
        }
        if (project.taskCategories) {
          // Group task categories by project (only for non-archived projects)
          if (!taskCategoriesByProject[project.title]) {
            taskCategoriesByProject[project.title] = []
          }

          project.taskCategories.forEach(category => {
            // Add to task categories grouped by project
            taskCategoriesByProject[project.title].push({
              id: category.id,
              name: category.name,
            })

            if (category.workflow?.statuses) {
              const workflowKey = `${project.id}_${category.workflow.id}`
              if (!grouped[workflowKey]) {
                grouped[workflowKey] = {
                  project: {
                    id: project.id,
                    title: project.title,
                  },
                  workflow: {
                    id: category.workflow.id,
                    name: category.workflow.name,
                  },
                  taskCategory: {
                    id: category.id,
                    name: category.name,
                  },
                  statuses: [],
                }
              }
              category.workflow.statuses.forEach(status => {
                if (!grouped[workflowKey].statuses.find(s => s.id === status.id)) {
                  grouped[workflowKey].statuses.push(status)
                  if (!allStatuses.find(s => s.id === status.id)) {
                    allStatuses.push(status)
                  }
                }
              })
            }
          })
        }
      })

      // Sort statuses within each workflow
      Object.keys(grouped).forEach(key => {
        grouped[key].statuses.sort((a, b) => a.position - b.position)
      })

      groupedWorkflowStatuses.value = grouped
      availableWorkflowStatuses.value = allStatuses.sort((a, b) => a.position - b.position)
      groupedTaskCategories.value = taskCategoriesByProject
      // Set labels for personal boards (collected from all user projects)
      // Set labels if board is personal board, or if board isn't loaded yet (will be checked when board loads)
      // This ensures labels are available even if the watcher runs before board is loaded
      if (!board.value || (board.value && board.value.isPersonalBoard)) {
        projectLabels.value = allLabels
        members.value = Array.from(membersById.values()).sort((a, b) => {
          const aTitle = (a.username || a.name || '').toLowerCase()
          const bTitle = (b.username || b.name || '').toLowerCase()
          return aTitle.localeCompare(bTitle)
        })
        // Set projects for filtering (only for personal boards)
        userProjects.value = projectsList.sort((a, b) => a.title.localeCompare(b.title))
      }
    }
  },
  { immediate: true }
)

// Load workflow statuses - trigger the lazy query
function loadWorkflowStatuses() {
  if (board.value?.isProjectBoard) {
    // Ensure projectId is set
    if (!workflowStatusesProjectId.value) {
      workflowStatusesProjectId.value =
        props.projectId || board.value.projectId || board.value.project?.id
    }
    if (workflowStatusesProjectId.value) {
      // Just call load() - variables are provided in useLazyQuery definition
      loadProjectWorkflowStatusesQuery()
    }
  } else if (board.value?.isPersonalBoard) {
    loadUserWorkflowStatusesQuery()
  }
}

// Column management functions
function openCreateColumnModal() {
  editingColumn.value = null
  columnForm.value = {
    title: '',
    color: '',
    position: board.value?.boardColumns?.length || 0,
    workflowStatusIds: [],
  }
  columnFormError.value = ''
  // Ensure workflow statuses are loaded when opening the modal
  if (board.value?.isProjectBoard && Object.keys(groupedWorkflowStatuses.value).length === 0) {
    loadWorkflowStatuses()
  }
  showColumnModal.value = true
}

function editColumn(column) {
  editingColumn.value = column
  columnForm.value = {
    title: column.title || '',
    color: column.color || '',
    position: column.position || 0,
    workflowStatusIds: column.workflowStatuses?.map(s => s.id) || [],
  }
  columnFormError.value = ''
  // Ensure workflow statuses are loaded when opening the modal
  if (board.value?.isProjectBoard && Object.keys(groupedWorkflowStatuses.value).length === 0) {
    loadWorkflowStatuses()
  }
  showColumnModal.value = true
}

function closeColumnModal() {
  showColumnModal.value = false
  editingColumn.value = null
  columnForm.value = {
    title: '',
    color: '',
    position: 0,
    workflowStatusIds: [],
  }
  columnFormError.value = ''
}

function closeColumnManagement() {
  showColumnManagement.value = false
}

function confirmDeleteColumn(column) {
  deletingColumn.value = column
}

async function handleColumnSubmit() {
  columnFormError.value = ''

  if (editingColumn.value) {
    // Update column
    updatingColumn.value = true
    try {
      // First update column basic info
      const { data: updateData } = await updateColumnMutation({
        id: editingColumn.value.id,
        title: columnForm.value.title,
        position: columnForm.value.position,
        color: columnForm.value.color || null,
      })

      if (updateData?.updateBoardColumn?.errors?.length) {
        const errorMessages = updateData.updateBoardColumn.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        columnFormError.value = errorMessages
        updatingColumn.value = false
        return
      }

      // Then update workflow statuses
      const currentStatusIds = editingColumn.value.workflowStatuses?.map(s => s.id) || []
      const newStatusIds = columnForm.value.workflowStatusIds || []
      const toAdd = newStatusIds.filter(id => !currentStatusIds.includes(id))
      const toRemove = currentStatusIds.filter(id => !newStatusIds.includes(id))

      if (toAdd.length > 0 || toRemove.length > 0) {
        const { data: statusData } = await updateColumnWorkflowStatusesMutation({
          columnId: editingColumn.value.id,
          addWorkflowStatusIds: toAdd,
          removeWorkflowStatusIds: toRemove,
        })

        if (statusData?.updateBoardColumnWorkflowStatuses?.errors?.length) {
          const errorMessages = statusData.updateBoardColumnWorkflowStatuses.errors
            .map(e => (typeof e === 'string' ? e : e.message || String(e)))
            .join(', ')
          columnFormError.value = errorMessages
          updatingColumn.value = false
          return
        }
      }

      closeColumnModal()
      await refetchBoard()
    } catch (e) {
      columnFormError.value = e.message || s__('Boards|Failed to update column')
    } finally {
      updatingColumn.value = false
    }
  } else {
    // Create column
    creatingColumn.value = true
    try {
      const { data } = await createColumnMutation({
        boardId: props.boardId,
        title: columnForm.value.title,
        position: columnForm.value.position,
        color: columnForm.value.color || null,
      })

      if (data?.createBoardColumn?.errors?.length) {
        const errorMessages = data.createBoardColumn.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        columnFormError.value = errorMessages
        creatingColumn.value = false
        return
      }

      const newColumnId = data?.createBoardColumn?.column?.id

      // Assign workflow statuses if any
      if (newColumnId && columnForm.value.workflowStatusIds?.length > 0) {
        const { data: statusData } = await updateColumnWorkflowStatusesMutation({
          columnId: newColumnId,
          addWorkflowStatusIds: columnForm.value.workflowStatusIds,
          removeWorkflowStatusIds: [],
        })

        if (statusData?.updateBoardColumnWorkflowStatuses?.errors?.length) {
          const errorMessages = statusData.updateBoardColumnWorkflowStatuses.errors
            .map(e => (typeof e === 'string' ? e : e.message || String(e)))
            .join(', ')
          columnFormError.value = errorMessages
          creatingColumn.value = false
          return
        }
      }

      closeColumnModal()
      await refetchBoard()
    } catch (e) {
      columnFormError.value = e.message || s__('Boards|Failed to create column')
    } finally {
      creatingColumn.value = false
    }
  }
}

async function confirmDeleteColumnAction() {
  if (!deletingColumn.value) return

  deleting.value = true
  try {
    const { data } = await deleteColumnMutation({
      id: deletingColumn.value.id,
    })

    if (data?.deleteBoardColumn?.errors?.length) {
      const errorMessages = data.deleteBoardColumn.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      alert(errorMessages)
      deleting.value = false
      return
    }

    deletingColumn.value = null
    await refetchBoard()
  } catch (e) {
    alert(e.message || s__('Boards|Failed to delete column'))
  } finally {
    deleting.value = false
  }
}

// Filter management functions
function buildQuickRoleFilters() {
  if (filterAllAccessible.value) {
    return {
      asAuthor: true,
      asAssignee: true,
      asSubscriber: true,
      allAccessible: true,
    }
  }

  return {
    asAuthor: filterAsAuthor.value,
    asAssignee: filterAsAssignee.value,
    asSubscriber: filterAsSubscriber.value,
    allAccessible: false,
  }
}

async function applyBoardSessionFilters(nextFilters) {
  sessionFilters.value = nextFilters

  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  await nextTick()
  await refetchBoard()
}

async function handleFilterApply(filters) {
  filterFormError.value = ''
  try {
    const search = filters.search
    if (search && search.trim().length > 0 && search.trim().length < 3) {
      filterFormError.value = s__('TaskFilter|Search requires at least 3 characters')
      return
    }

    const advancedFilters = convertFiltersToBoardFormat(filters)
    const nextFilters = {
      ...advancedFilters,
      ...buildQuickRoleFilters(),
    }
    await applyBoardSessionFilters(nextFilters)
  } catch (e) {
    filterFormError.value = e.message || s__('Boards|Failed to apply session filters')
  }
}

async function toggleQuickRole(roleName) {
  if (roleName === 'all') {
    if (!filterAllAccessible.value) {
      lastManualQuickRoles.value = {
        filterAsAuthor: filterAsAuthor.value,
        filterAsAssignee: filterAsAssignee.value,
        filterAsSubscriber: filterAsSubscriber.value,
      }
      filterAsAuthor.value = true
      filterAsAssignee.value = true
      filterAsSubscriber.value = true
      filterAllAccessible.value = true
    } else {
      filterAllAccessible.value = false
      const restore = {
        filterAsAuthor: lastManualQuickRoles.value.filterAsAuthor,
        filterAsAssignee: lastManualQuickRoles.value.filterAsAssignee,
        filterAsSubscriber: lastManualQuickRoles.value.filterAsSubscriber,
      }
      if (!restore.filterAsAuthor && !restore.filterAsAssignee && !restore.filterAsSubscriber) {
        restore.filterAsAuthor = true
      }
      filterAsAuthor.value = restore.filterAsAuthor
      filterAsAssignee.value = restore.filterAsAssignee
      filterAsSubscriber.value = restore.filterAsSubscriber
    }
  } else {
    if (filterAllAccessible.value) return

    if (roleName === 'mine') filterAsAuthor.value = !filterAsAuthor.value
    if (roleName === 'assigned') filterAsAssignee.value = !filterAsAssignee.value
    if (roleName === 'watching') filterAsSubscriber.value = !filterAsSubscriber.value

    if (!filterAsAuthor.value && !filterAsAssignee.value && !filterAsSubscriber.value) {
      filterAsAuthor.value = true
    }

    lastManualQuickRoles.value = {
      filterAsAuthor: filterAsAuthor.value,
      filterAsAssignee: filterAsAssignee.value,
      filterAsSubscriber: filterAsSubscriber.value,
    }
  }

  const currentFilters = await getCurrentFiltersFromPanel()
  await handleFilterApply(currentFilters)
}

function convertFiltersToBoardFormat(filters) {
  const result = {}

  const search = filters.search
  if (search && search.trim().length >= 3) {
    result.search = search.trim()
  }

  // Multi-select filters (OR logic)
  // Always include multi-select fields when provided, so empty arrays can override pre-filters.
  if (filters.authorIds !== undefined) {
    result.authorIds =
      filters.authorIds && filters.authorIds.length > 0
        ? filters.authorIds.map(id => String(id))
        : []
  }
  if (filters.assigneeIds !== undefined) {
    result.assigneeIds =
      filters.assigneeIds && filters.assigneeIds.length > 0
        ? filters.assigneeIds.map(id => (id === 'none' ? id : String(id)))
        : []
  }
  if (filters.priorities !== undefined) {
    // Keep priorities as-is (they're already lowercase strings like "high", "medium", or "none")
    result.priorities =
      filters.priorities && filters.priorities.length > 0 ? filters.priorities : []
  }
  if (filters.workflowStatusIds !== undefined) {
    result.workflowStatusIds =
      filters.workflowStatusIds && filters.workflowStatusIds.length > 0
        ? filters.workflowStatusIds.map(id => String(id))
        : []
  }
  if (filters.taskCategoryIds !== undefined) {
    result.taskCategoryIds =
      filters.taskCategoryIds && filters.taskCategoryIds.length > 0
        ? filters.taskCategoryIds.map(id => String(id))
        : []
  }

  // Always include labelIds, personalLabelIds, and projectIds to override pre-filters
  // If empty array, it will override pre-filter; if not present, pre-filter remains
  if (filters.labelIds !== undefined) {
    if (filters.labelIds && filters.labelIds.length > 0) {
      // Ensure label IDs are strings (GraphQL ID type expects strings)
      result.labelIds = filters.labelIds.map(id => String(id))
    } else {
      // Explicitly set empty array to override pre-filter
      result.labelIds = []
    }
  }
  if (filters.personalLabelIds !== undefined) {
    if (filters.personalLabelIds && filters.personalLabelIds.length > 0) {
      // Ensure personal label IDs are strings (GraphQL ID type expects strings)
      result.personalLabelIds = filters.personalLabelIds.map(id => String(id))
    } else {
      // Explicitly set empty array to override pre-filter
      result.personalLabelIds = []
    }
  }
  if (filters.projectIds !== undefined) {
    if (filters.projectIds && filters.projectIds.length > 0) {
      // Ensure project IDs are strings (GraphQL ID type expects strings)
      result.projectIds = filters.projectIds.map(id => String(id))
    } else {
      // Explicitly set empty array to override pre-filter
      result.projectIds = []
    }
  }
  if (filters.dueFrom) {
    result.dueFrom = filters.dueFrom
  }
  if (filters.dueTo) {
    result.dueTo = filters.dueTo
  }
  if (filters.dueInDays) {
    result.dueInDays = filters.dueInDays
  }
  if (filters.includeOverdue !== undefined) {
    result.includeOverdue = filters.includeOverdue
  }
  // Note: includeFinished is not used in boards - users control this via workflow statuses in columns
  if (filters.includeArchived !== undefined) {
    result.includeArchived = filters.includeArchived
  }
  // Note: includeFinished is not used in boards - users control this via workflow statuses in columns

  return result
}

// Get current filter values from TaskFilterPanel with multiple fallback strategies
// This function tries multiple approaches to ensure reliability
async function getCurrentFiltersFromPanel() {
  // Ensure Vue has updated the ref
  await nextTick()

  // Strategy 1: Try to get filters from exposed method (most reliable)
  // Try multiple times with small delays to ensure component is ready
  for (let attempt = 0; attempt < 3; attempt++) {
    const filterPanel = filterPanelRef.value
    if (filterPanel) {
      // In Vue 3, defineExpose methods are directly on the component instance
      const getFiltersMethod = filterPanel.getCurrentFilters

      if (typeof getFiltersMethod === 'function') {
        try {
          const filters = getFiltersMethod()
          if (filters && typeof filters === 'object') {
            // Validate that we got a proper filters object
            if ('search' in filters || 'priority' in filters || 'workflowStatusId' in filters) {
              return filters
            }
          }
        } catch (e) {
          // Failed to get filters from exposed method, will try fallback
        }
      }
    }

    // Wait a bit before retrying
    if (attempt < 2) {
      await new Promise(resolve => setTimeout(resolve, 30))
    }
  }

  // Strategy 2: Read directly from DOM inputs (fallback if method not available)
  const filterPanelEl = filterPanelRef.value?.$el
  if (filterPanelEl) {
    try {
      const filters = readFiltersFromDOM(filterPanelEl)
      if (filters && typeof filters === 'object') {
        return filters
      }
    } catch (e) {
      // Failed to read filters from DOM, will use computed property
    }
  }

  // Strategy 3: Use computed property as last resort
  // This should always work as it's a computed property
  const fallbackFilters = currentFiltersForPanel.value
  if (fallbackFilters && typeof fallbackFilters === 'object') {
    return fallbackFilters
  }

  // Ultimate fallback: return empty filters object
  return {
    search: '',
    authorIds: [],
    assigneeIds: [],
    priorities: [],
    workflowStatusIds: [],
    taskCategoryIds: [],
    labelIds: [],
    personalLabelIds: [],
    projectIds: [],
    dueFrom: '',
    dueTo: '',
    dueInDays: null,
    includeOverdue: true,
    includeFinished: false,
    includeArchived: false,
  }
}

// Helper function to read filter values directly from DOM
function readFiltersFromDOM(panelEl) {
  const filters = {
    search: '',
    authorIds: [],
    assigneeIds: [],
    priorities: [],
    workflowStatusIds: [],
    taskCategoryIds: [],
    labelIds: [],
    personalLabelIds: [],
    projectIds: [],
    dueFrom: '',
    dueTo: '',
    dueInDays: null,
    includeOverdue: true,
    includeFinished: false,
    includeArchived: false,
  }

  // Read search input
  const searchInput = panelEl.querySelector('input[type="text"]')
  if (
    searchInput &&
    searchInput.placeholder &&
    searchInput.placeholder.toLowerCase().includes('search')
  ) {
    filters.search = searchInput.value || ''
  }

  // Read priority select (first select is usually priority)
  const selects = Array.from(panelEl.querySelectorAll('select'))
  if (selects.length > 0) {
    const prioritySelect = selects[0]
    const priorityValue = prioritySelect.value
    if (priorityValue && priorityValue !== '') {
      filters.priority = priorityValue
    }
  }

  // Read workflow status (could be in a grouped select or second select)
  // Look for selects with workflow status options (usually the second select)
  selects.forEach((select, index) => {
    const options = Array.from(select.querySelectorAll('option'))
    const hasStatusOptions = options.some(opt => {
      const value = opt.value
      return value && value !== '' && !isNaN(parseInt(value))
    })

    // Check if this select has optgroups (grouped workflow statuses)
    const hasOptgroups = select.querySelectorAll('optgroup').length > 0

    if (hasStatusOptions && (hasOptgroups || index > 0)) {
      const statusValue = select.value
      if (statusValue && statusValue !== '') {
        // Convert to number if it's a numeric string
        filters.workflowStatusId = isNaN(parseInt(statusValue))
          ? statusValue
          : parseInt(statusValue)
      }
    }
  })

  // Read task category select
  selects.forEach(select => {
    const options = Array.from(select.querySelectorAll('option'))
    const hasCategoryOptions = options.some(opt => {
      const value = opt.value
      return value && value !== '' && !isNaN(parseInt(value))
    })

    if (hasCategoryOptions) {
      const categoryValue = select.value
      if (categoryValue && categoryValue !== '' && !filters.workflowStatusId) {
        // Only set if not already set as workflow status
        filters.taskCategoryId = categoryValue
      }
    }
  })

  // Read date inputs
  const dateInputs = Array.from(panelEl.querySelectorAll('input[type="date"]'))
  if (dateInputs.length >= 1) {
    filters.dueFrom = dateInputs[0].value || ''
  }
  if (dateInputs.length >= 2) {
    filters.dueTo = dateInputs[1].value || ''
  }

  // Read checkboxes
  const checkboxes = Array.from(panelEl.querySelectorAll('input[type="checkbox"]'))
  checkboxes.forEach(checkbox => {
    const label = checkbox.closest('label')
    if (label) {
      const labelText = (label.textContent || '').toLowerCase()
      // Note: includeFinished is not used in boards - users control this via workflow statuses in columns
      if (labelText.includes('archived')) {
        filters.includeArchived = checkbox.checked
      }
    }
  })

  // For author, assignee, and labels - these are complex components
  // We'll use the current computed values as they're managed by child components
  const currentFilters = currentFiltersForPanel.value
  filters.authorId = currentFilters.authorId
  filters.assigneeId = currentFilters.assigneeId
  // Ensure label IDs are strings (GraphQL ID type expects strings)
  filters.labelIds = (currentFilters.labelIds || []).map(id => String(id))
  filters.personalLabelIds = (currentFilters.personalLabelIds || []).map(id => String(id))
  filters.projectIds = (currentFilters.projectIds || []).map(id => String(id))
  // Note: includeFinished is not used in boards - users control this via workflow statuses in columns
  filters.includeFinished = false // Always false for boards

  return filters
}

// Ordering management functions
async function handleOrderingChange({ orderBy, orderDirection }) {
  if (!board.value || !boardLoaded) return

  // Skip if values are empty (using board defaults)
  if (!orderBy && !orderDirection) return

  // Update session ordering values
  sessionOrderBy.value = orderBy || ''
  sessionOrderDirection.value = orderDirection || ''

  // Store board URL with session parameters
  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  try {
    await refetchBoard()
  } catch (e) {
    orderingFormError.value = e.message || s__('Boards|Failed to apply ordering')
  }
}

const selectedSwimlaneMode = computed({
  get: () => sessionSwimlaneMode.value || board.value?.swimlaneMode || 'none',
  set: value => {
    sessionSwimlaneMode.value = value || 'none'
  },
})

const swimlaneModeOptions = computed(() => {
  const options = [
    { value: 'none', label: s__('Boards|None') },
    { value: 'by_assignee', label: s__('Boards|By Assignee') },
    { value: 'by_author', label: s__('Boards|By Author') },
    { value: 'by_due_date', label: s__('Boards|By Due Date') },
    { value: 'by_category', label: s__('Boards|By Category') },
    { value: 'by_status', label: s__('Boards|By Status') },
    { value: 'by_priority', label: s__('Boards|By Priority') },
  ]
  if (board.value?.isPersonalBoard) {
    options.push({ value: 'by_project', label: s__('Boards|By Project') })
  }
  return options
})

async function resetSessionOrdering() {
  // Reset to empty (which means use board's default values)
  sessionOrderBy.value = ''
  sessionOrderDirection.value = ''

  // Store board URL with session parameters
  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  await nextTick()

  await refetchBoard()
}

// Watch for changes in ordering and refetch immediately
watch([sessionOrderBy, sessionOrderDirection], async () => {
  if (!board.value || !boardLoaded) return

  // Skip if values are empty (using board defaults)
  if (!sessionOrderBy.value && !sessionOrderDirection.value) return

  // Store board URL with session parameters
  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  try {
    await refetchBoard()
  } catch (e) {
    orderingFormError.value = e.message || s__('Boards|Failed to apply ordering')
  }
})

// Watch for changes in swimlane mode and refetch immediately
watch(sessionSwimlaneMode, async () => {
  if (!board.value || !boardLoaded) return

  // Store board URL with session parameters
  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  try {
    await refetchBoard()
  } catch (e) {
    swimlaneFormError.value = e.message || s__('Boards|Failed to apply swimlane mode')
  }
})

async function restoreSavedBoardSettings() {
  sessionFilters.value = null
  sessionOrderBy.value = ''
  sessionOrderDirection.value = ''
  sessionSwimlaneMode.value = ''
  syncQuickRolesFromMergedFilters()

  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  await nextTick()
  await refetchBoard()
}

async function saveBoardDefaults() {
  if (!board.value?.canManageBoard) return
  savingBoardDefaults.value = true
  filterFormError.value = ''
  orderingFormError.value = ''
  swimlaneFormError.value = ''

  try {
    const filters = await getCurrentFiltersFromPanel()
    const search = filters.search
    if (search && search.trim().length > 0 && search.trim().length < 3) {
      filterFormError.value = s__('TaskFilter|Search requires at least 3 characters')
      savingBoardDefaults.value = false
      return
    }

    const preFilters = {
      ...convertFiltersToBoardFormat(filters),
      ...buildQuickRoleFilters(),
    }

    const orderBy = sessionOrderBy.value || boardDefaultOrderBy.value || 'created_at'
    const orderDirection = sessionOrderDirection.value || boardDefaultOrderDirection.value || 'asc'
    const swimlaneMode = sessionSwimlaneMode.value || board.value?.swimlaneMode || 'none'

    const { data } = await updateBoardMutation({
      id: props.boardId,
      preFilters,
      orderBy,
      orderDirection,
      swimlaneMode,
    })

    if (data?.updateBoard?.errors?.length) {
      const errorMessages = data.updateBoard.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      filterFormError.value = errorMessages
      return
    }

    await restoreSavedBoardSettings()
  } catch (e) {
    filterFormError.value = e.message || s__('Boards|Failed to save board settings')
  } finally {
    savingBoardDefaults.value = false
  }
}

function person(u) {
  return u?.username ? '@' + u.username : u?.name || (u ? s__('Boards|User') + ' ' + u.id : '—')
}

function getTaskProjectRole(task, type) {
  if (!task || !type) return null
  const userId = type === 'assignee' ? task.assignee?.id : task.author?.id
  if (!userId) return null
  return getProjectRole(projectRolesMap.value, userId)
}

function getEffectiveSwimlaneMode() {
  return sessionSwimlaneMode.value || board.value?.swimlaneMode || 'none'
}

function handleBoardScroll() {
  updateActiveMobileColumnFromScroll()
}

watch(
  [() => board.value?.boardColumns, sessionSwimlaneMode, () => board.value?.swimlaneMode],
  async () => {
    ensureActiveMobileColumn()
    await nextTick()
    updateActiveMobileColumnFromScroll()
  },
  { immediate: true }
)

// Variables for load more query
// Initialize with props.boardId to ensure valid GraphQL document initialization
// The boardId will be updated before calling load() if needed, but this ensures the query is valid from the start
const loadMoreVariables = ref({
  boardId: props.boardId ? String(props.boardId).trim() : '',
  columnId: '',
  first: 20,
  after: null,
  sessionFilters: null,
  sessionOrderBy: null,
  sessionOrderDirection: null,
})

// Lazy query for loading more tasks
// Variables are updated right before calling load() to ensure each column uses its own cursor
// We always set valid variables before calling load(), so this function should always return valid vars
// Use onResult to capture the result directly and avoid Apollo cache normalization issues
let pendingLoadMoreResolver = null
let pendingLoadMoreColumnId = null

const {
  load: loadMoreTasksQuery,
  result: loadMoreResult,
  onResult: onLoadMoreResult,
} = useLazyQuery(LOAD_MORE_COLUMN_TASKS, () => loadMoreVariables.value, {
  fetchPolicy: 'network-only', // Always fetch from network, don't use cache
  notifyOnNetworkStatusChange: false,
})

// Capture result directly when query completes - this avoids Apollo cache normalization issues
onLoadMoreResult(result => {
  if (result.data) {
    const columnId = loadMoreVariables.value.columnId

    // Resolve pending promise if waiting
    if (pendingLoadMoreResolver && String(columnId) === String(pendingLoadMoreColumnId)) {
      pendingLoadMoreResolver(result.data)
      pendingLoadMoreResolver = null
      pendingLoadMoreColumnId = null
    }
  }
})

// Track which column is currently loading to prevent race conditions
let loadingColumnId = null

// Variables for load more swimlane query
const loadMoreSwimlaneVariables = ref({
  boardId: null,
  columnId: null,
  swimlaneKey: null,
  first: 20,
  after: null,
  sessionFilters: null,
  sessionOrderBy: null,
  sessionOrderDirection: null,
})

// Lazy query for loading more swimlane tasks
const { load: loadMoreSwimlaneTasksQuery, result: loadMoreSwimlaneResult } = useLazyQuery(
  LOAD_MORE_SWIMLANE_TASKS,
  () => loadMoreSwimlaneVariables.value
)

async function loadMoreTasks(columnId) {
  const pagination = columnPagination.value[columnId]
  if (!pagination || !pagination.hasMore || pagination.loading || pagination.exhausted) {
    return
  }

  // Prevent concurrent loads from different columns
  if (loadingColumnId !== null && loadingColumnId !== columnId) {
    return
  }

  // Set loading state
  pagination.loading = true
  loadingColumnId = columnId

  try {
    // If cursor is null, generate it from the last task
    let cursor = pagination.cursor
    if (!cursor && columnTasks.value[columnId]?.length > 0) {
      const lastTask = columnTasks.value[columnId][columnTasks.value[columnId].length - 1]
      if (lastTask?.id) {
        // Generate cursor from last task ID (base64 encode)
        cursor = btoa(lastTask.id.toString())
      }
    }

    // Validate cursor: if cursor exists, verify the cursor task is actually in this column
    // This prevents using cursors from other columns or invalid cursors
    // NOTE: We don't check if cursor is the "last task" here because the cursor is the last task
    // from the PREVIOUS page, not necessarily the last task overall. We'll validate after loading.
    if (cursor) {
      try {
        const decodedCursor = atob(cursor)
        const cursorTaskId = parseInt(decodedCursor, 10)
        const initialTasks = columnTasks.value[columnId] || []
        const loadedTasks = loadedColumnTasks.value[columnId] || []
        const allCurrentTasks = [...initialTasks, ...loadedTasks]
        const cursorTaskExists = allCurrentTasks.some(t => t.id === String(cursorTaskId))

        if (!cursorTaskExists) {
          console.warn(
            `[LoadMore] Column ${columnId}: Cursor task (${cursorTaskId}) not found in column's current tasks - cursor is invalid`
          )
          console.warn(
            `[LoadMore] Column ${columnId}: Resetting cursor to null and setting hasMore to false`
          )
          cursor = null
          // Also reset hasMore to false since the cursor is invalid
          pagination.hasMore = false
          pagination.loading = false
          if (loadingColumnId === columnId) {
            loadingColumnId = null
          }
          return
        }
      } catch (e) {
        console.warn(`[LoadMore] Column ${columnId}: Failed to validate cursor:`, e)
        console.warn(`[LoadMore] Column ${columnId}: Resetting cursor to null`)
        cursor = null
        pagination.hasMore = false
        pagination.loading = false
        if (loadingColumnId === columnId) {
          loadingColumnId = null
        }
        return
      }
    }

    // Prepare variables for this specific column's load more request
    // Store the columnId we're loading for to verify we process the correct column
    // Convert to string to ensure consistent comparison with GraphQL response
    const targetColumnId = String(columnId)

    // Ensure we have a valid board ID
    if (!board.value || !board.value.id) {
      console.error('Cannot load more tasks: board ID is missing')
      pagination.loading = false
      if (loadingColumnId === columnId) {
        loadingColumnId = null
      }
      return
    }

    const variablesForThisRequest = {
      boardId: String(board.value.id),
      columnId: String(columnId),
      first: 20,
      after: cursor || null,
      sessionFilters:
        sessionFilters.value &&
        typeof sessionFilters.value === 'object' &&
        Object.keys(sessionFilters.value).length > 0
          ? sessionFilters.value
          : null,
      sessionOrderBy: (() => {
        if (!sessionOrderBy.value || sessionOrderBy.value === '') return null
        const boardOrderBy = boardDefaultOrderBy.value || 'created_at'
        return sessionOrderBy.value !== boardOrderBy ? sessionOrderBy.value : null
      })(),
      sessionOrderDirection: (() => {
        if (!sessionOrderDirection.value || sessionOrderDirection.value === '') return null
        const boardOrderDirection = boardDefaultOrderDirection.value || 'asc'
        return sessionOrderDirection.value !== boardOrderDirection
          ? sessionOrderDirection.value
          : null
      })(),
    }

    // Update shared variables ref right before calling load()
    // This minimizes the window for race conditions - the variables are read synchronously by load()
    loadMoreVariables.value = variablesForThisRequest

    // Create a promise that will resolve when onResult callback fires with the correct column data
    const resultPromise = new Promise(resolve => {
      pendingLoadMoreResolver = resolve
      pendingLoadMoreColumnId = String(targetColumnId)
    })

    // Call load() - it will use the variables from loadMoreVariables.value via the reactive function
    // The load() function returns a promise that resolves when the query completes
    await loadMoreTasksQuery()

    // Wait for the onResult callback to fire (most reliable) or timeout after 2 seconds
    // The onResult callback captures the raw response before Apollo normalizes the cache
    const finalResult = await Promise.race([
      resultPromise,
      new Promise(resolve => {
        setTimeout(() => {
          console.warn(
            `[LoadMore] Column ${columnId}: onResult callback timeout, using reactive ref`
          )
          resolve(loadMoreResult.value)
        }, 2000)
      }),
    ])

    // Clear pending resolver if it wasn't used
    if (pendingLoadMoreResolver) {
      pendingLoadMoreResolver = null
      pendingLoadMoreColumnId = null
    }

    // CRITICAL: Verify the variables match what we expect
    if (String(loadMoreVariables.value.columnId) !== String(targetColumnId)) {
      console.error(
        `[LoadMore] Column ${columnId}: VARIABLE MISMATCH! Variables have columnId=${loadMoreVariables.value.columnId}, but targetColumnId=${targetColumnId}`
      )
    }

    // CRITICAL: The GraphQL query now only returns the specific column we requested
    // This prevents issues with cursors from different columns interfering with each other
    if (finalResult?.board?.boardColumns) {
      // Get the column data (should be exactly one column)
      // Compare as strings to handle type mismatches (GraphQL might return ID as number or string)
      const columnData = finalResult.board.boardColumns.find(
        col => String(col.id) === String(targetColumnId)
      )

      if (!columnData) {
        console.error(`[LoadMore] Column ${targetColumnId}: Column not found in response`)
        pagination.loading = false
        if (loadingColumnId === columnId) {
          loadingColumnId = null
        }
        return
      }

      if (columnData?.tasksPaginated) {
        let newTasks = columnData.tasksPaginated.tasks || []

        // Handle case where cursor task is not found in column (backend returns empty results)
        // This can happen if the cursor was from a different column or task was moved
        if (newTasks.length === 0 && cursor) {
          try {
            const decodedCursor = atob(cursor)
            const cursorTaskId = parseInt(decodedCursor, 10)

            // Check if we have any initial tasks for this column
            const initialTasks = columnTasks.value[targetColumnId] || []
            const loadedTasks = loadedColumnTasks.value[targetColumnId] || []
            const allCurrentTasks = [...initialTasks, ...loadedTasks]

            if (allCurrentTasks.length > 0) {
              // Check if cursor task exists in current tasks
              const cursorTaskExists = allCurrentTasks.some(t => t.id === String(cursorTaskId))

              // We have tasks, so generate cursor from the last task we actually have
              const lastTask = allCurrentTasks[allCurrentTasks.length - 1]
              if (lastTask?.id) {
                // Check if cursor task is the last task - if so, there are no more tasks
                const isLastTask = cursorTaskExists && String(lastTask.id) === String(cursorTaskId)
                if (isLastTask) {
                  // Don't retry, just proceed to set hasMore: false (handled below in pagination update)
                } else {
                  const newCursor = btoa(lastTask.id.toString())

                  // Check if the new cursor is different from the invalid one
                  if (newCursor !== cursor) {
                    // Retry with the correct cursor
                    const retryVariables = {
                      ...variablesForThisRequest,
                      after: newCursor,
                    }
                    loadMoreVariables.value = retryVariables
                    const retryQueryResult = await loadMoreTasksQuery()
                    const retryResult = retryQueryResult?.data || loadMoreResult.value

                    // Process the retry result
                    if (retryResult?.board?.boardColumns) {
                      const retryColumnData = retryResult.board.boardColumns.find(
                        col => String(col.id) === String(targetColumnId)
                      )
                      if (retryColumnData?.tasksPaginated) {
                        newTasks = retryColumnData.tasksPaginated.tasks || []

                        // Update columnData to use retry data for pagination update later
                        columnData.tasksPaginated = retryColumnData.tasksPaginated
                      } else {
                        console.warn(
                          `[LoadMore] Column ${targetColumnId}: Retry query returned no tasksPaginated data`
                        )
                      }
                    } else {
                      console.warn(
                        `[LoadMore] Column ${targetColumnId}: Retry query returned no board columns`
                      )
                    }
                  } else {
                    console.warn(
                      `[LoadMore] Column ${targetColumnId}: New cursor same as invalid cursor, cannot retry`
                    )
                  }
                }
              } else {
                console.warn(
                  `[LoadMore] Column ${targetColumnId}: Last task has no ID, cannot regenerate cursor`
                )
              }
            } else {
              // No tasks at all - this shouldn't happen if hasMore is true
              console.warn(
                `[LoadMore] Column ${targetColumnId}: No current tasks but hasMore is true - this is unexpected`
              )
              console.warn(`[LoadMore] Column ${targetColumnId}: Pagination state:`, pagination)

              // Try loading from beginning anyway
              const retryVariables = {
                ...variablesForThisRequest,
                after: null, // Reset cursor
              }
              loadMoreVariables.value = retryVariables
              const retryQueryResult = await loadMoreTasksQuery()
              const retryResult = retryQueryResult?.data || loadMoreResult.value

              // Process the retry result
              if (retryResult?.board?.boardColumns) {
                const retryColumnData = retryResult.board.boardColumns.find(
                  col => String(col.id) === String(targetColumnId)
                )
                if (retryColumnData?.tasksPaginated) {
                  newTasks = retryColumnData.tasksPaginated.tasks || []

                  // Update columnData to use retry data for pagination update later
                  columnData.tasksPaginated = retryColumnData.tasksPaginated
                } else {
                  console.warn(
                    `[LoadMore] Column ${targetColumnId}: Retry from beginning returned no tasksPaginated data`
                  )
                }
              } else {
                console.warn(
                  `[LoadMore] Column ${targetColumnId}: Retry from beginning returned no board columns`
                )
              }
            }
          } catch (e) {
            console.warn(`[LoadMore] Column ${targetColumnId}: Failed to handle invalid cursor:`, e)
          }
        }

        // WORKAROUND: Filter out the cursor task itself
        // The backend sometimes returns the cursor task in the results, which shouldn't happen
        // We filter it out client-side as a safety measure
        if (cursor && newTasks.length > 0) {
          try {
            const decodedCursor = atob(cursor)
            const cursorTaskId = parseInt(decodedCursor, 10)

            // Filter out the cursor task itself (it should not be in "next page" results)
            const tasksBeforeFilter = newTasks.length
            newTasks = newTasks.filter(t => t.id !== String(cursorTaskId))

            if (tasksBeforeFilter !== newTasks.length) {
              console.warn(
                `[LoadMore] Column ${targetColumnId}: Filtered out cursor task (${cursorTaskId}) from results`
              )
              console.warn(
                `[LoadMore] Column ${targetColumnId}: This indicates a backend issue - cursor task should not be in results`
              )
            }
          } catch (e) {
            console.warn(
              `[LoadMore] Column ${targetColumnId}: Failed to decode cursor, skipping filter:`,
              e
            )
          }
        }

        // Get initial tasks for duplicate checking
        const initialTasks = columnTasks.value[targetColumnId] || []
        const initialTaskIds = new Set(initialTasks.map(t => t.id))

        // Append new tasks to loaded tasks (filter duplicates)
        const currentLoaded = loadedColumnTasks.value[targetColumnId] || []
        const loadedTaskIds = new Set(currentLoaded.map(t => t.id))
        const allExistingIds = new Set([...initialTaskIds, ...loadedTaskIds])
        const uniqueNewTasks = newTasks.filter(t => !allExistingIds.has(t.id))

        loadedColumnTasks.value = {
          ...loadedColumnTasks.value,
          [targetColumnId]: [...currentLoaded, ...uniqueNewTasks],
        }

        // Update pagination state ONLY for the target column
        // CRITICAL: We must NOT touch any other column's pagination state
        // The query response contains pagination data for all columns using the same cursor,
        // but we only trust the data for the column we're loading more for
        const targetPagination = columnPagination.value[targetColumnId]
        if (targetPagination && targetPagination.loading) {
          // Only update if this is still the column we're loading for
          // This prevents race conditions if multiple columns are clicked rapidly
          const oldCursor = targetPagination.cursor

          // CRITICAL: If we got 0 unique new tasks, there are no more tasks to load
          // Set hasMore to false and mark column as exhausted
          // This handles cases where the backend incorrectly reports hasNextPage=true
          // when the cursor task is actually the last task in the column
          let actualHasMore = false
          let isExhausted = false
          if (uniqueNewTasks.length > 0) {
            // We got new tasks, trust the backend's hasNextPage
            actualHasMore = columnData.tasksPaginated.hasNextPage
            isExhausted = false
          } else {
            // We got 0 new tasks - always set hasMore to false and mark as exhausted
            // Trust the backend's hasNextPage when it's false
            // If backend says hasNextPage: true but returns 0 tasks, it's wrong
            actualHasMore = false
            isExhausted = true
          }

          targetPagination.cursor = columnData.tasksPaginated.endCursor
          targetPagination.hasMore = actualHasMore
          targetPagination.totalCount = columnData.tasksPaginated.totalCount
          targetPagination.exhausted = isExhausted

          // Force reactivity update
          columnPagination.value = { ...columnPagination.value }

          if (uniqueNewTasks.length === 0 && columnData.tasksPaginated.hasNextPage) {
            console.warn(
              `[LoadMore] Column ${targetColumnId}: Got 0 unique tasks but backend says hasMore=true - corrected to false`
            )
          }
        } else {
          console.warn(
            `[LoadMore] Column ${targetColumnId}: Pagination state not found or not loading!`
          )
        }

        // EXPLICITLY IGNORE pagination data for all other columns in the response
        // Do NOT process or update any other column's pagination state
        // Each column maintains its own cursor independently
      } else {
        console.warn(`[LoadMore] Column ${targetColumnId}: No tasksPaginated data in response`)
      }
    } else {
      console.warn(`[LoadMore] Column ${targetColumnId}: No board columns in response`)
    }
  } catch (err) {
    console.error('Failed to load more tasks:', err)
  } finally {
    // Only clear loading state if this is still the same column request
    const currentPagination = columnPagination.value[columnId]
    if (currentPagination) {
      currentPagination.loading = false
    }
    // Clear the loading column lock
    if (loadingColumnId === columnId) {
      loadingColumnId = null
    }
  }
}

async function loadMoreSwimlaneTasks(swimlaneKey, columnId) {
  const key = `${swimlaneKey}-${columnId}`

  // Ensure pagination exists (should be initialized when board loads, but create if missing)
  if (!swimlanePagination.value[key]) {
    // Find swimlane to get initial hasMore state
    const column = board.value?.boardColumns?.find(col => col.id === columnId)
    const swimlane = column?.swimlanes?.find(s => String(s.key) === String(swimlaneKey))
    swimlanePagination.value[key] = {
      cursor: null,
      hasMore: swimlane?.hasMore || false,
      loading: false,
      totalCount: swimlane?.totalCount || swimlane?.tasks?.length || 0,
    }
  }
  const pagination = swimlanePagination.value[key]

  if (!pagination.hasMore || pagination.loading) {
    return
  }

  // Set loading state
  pagination.loading = true

  try {
    // Get cursor from pagination state (should be set from previous load or initial load)
    let cursor = pagination.cursor

    if (!cursor) {
      // If no cursor in pagination, calculate from the last task in the combined list
      const column = board.value?.boardColumns?.find(col => col.id === columnId)
      const swimlane = column?.swimlanes?.find(s => String(s.key) === String(swimlaneKey))
      const initialTasks = swimlane?.tasks || []
      const loadedTasks = loadedSwimlaneTasks.value[key] || []

      // Combine initial and loaded tasks to get the actual last task
      const allTasks = [...initialTasks, ...loadedTasks]
      if (allTasks.length > 0) {
        const lastTask = allTasks[allTasks.length - 1]
        if (lastTask?.id) {
          cursor = btoa(lastTask.id.toString())
          // Update pagination cursor for next time
          pagination.cursor = cursor
        }
      }
    }

    // If still no cursor, we can't paginate
    if (!cursor) {
      pagination.loading = false
      return
    }

    // Update variables (cursor should point to the last task we already have)
    loadMoreSwimlaneVariables.value = {
      boardId: board.value.id,
      first: 20,
      after: cursor, // This cursor should be the ID of the last task we already have
      sessionFilters:
        sessionFilters.value &&
        typeof sessionFilters.value === 'object' &&
        Object.keys(sessionFilters.value).length > 0
          ? sessionFilters.value
          : null,
      sessionOrderBy: (() => {
        if (!sessionOrderBy.value || sessionOrderBy.value === '') return null
        const boardOrderBy = boardDefaultOrderBy.value || 'created_at'
        return sessionOrderBy.value !== boardOrderBy ? sessionOrderBy.value : null
      })(),
      sessionOrderDirection: (() => {
        if (!sessionOrderDirection.value || sessionOrderDirection.value === '') return null
        const boardOrderDirection = boardDefaultOrderDirection.value || 'asc'
        return sessionOrderDirection.value !== boardOrderDirection
          ? sessionOrderDirection.value
          : null
      })(),
      sessionSwimlaneMode: (() => {
        if (!sessionSwimlaneMode.value || sessionSwimlaneMode.value === '') return null
        const boardSwimlaneMode = board.value?.swimlaneMode || 'none'
        return sessionSwimlaneMode.value !== boardSwimlaneMode ? sessionSwimlaneMode.value : null
      })(),
    }

    await loadMoreSwimlaneTasksQuery()

    if (loadMoreSwimlaneResult.value?.board?.boardColumns) {
      const columnData = loadMoreSwimlaneResult.value.board.boardColumns.find(
        col => col.id === columnId
      )
      if (columnData?.swimlanes) {
        // Find swimlane by key (convert to string for comparison)
        const swimlaneData = columnData.swimlanes.find(s => String(s.key) === String(swimlaneKey))

        if (swimlaneData?.tasksPaginated) {
          const newTasks = swimlaneData.tasksPaginated.tasks || []

          // Get initial tasks to check for duplicates
          const column = board.value?.boardColumns?.find(col => col.id === columnId)
          const swimlane = column?.swimlanes?.find(s => String(s.key) === String(swimlaneKey))
          const initialTasks = swimlane?.tasks || []
          const initialTaskIds = new Set(initialTasks.map(t => t.id))

          // Append new tasks to loaded tasks
          const currentLoaded = loadedSwimlaneTasks.value[key] || []

          // Check for duplicates in both initialTasks and loadedTasks
          // The backend should handle pagination correctly, but we need to filter duplicates on frontend
          // to handle cases where initial tasks include tasks with IDs > cursor
          const loadedTaskIds = new Set(currentLoaded.map(t => t.id))
          const allExistingIds = new Set([...initialTaskIds, ...loadedTaskIds])
          const uniqueNewTasks = newTasks.filter(t => !allExistingIds.has(t.id))

          loadedSwimlaneTasks.value = {
            ...loadedSwimlaneTasks.value,
            [key]: [...currentLoaded, ...uniqueNewTasks],
          }

          // Update pagination state
          pagination.cursor = swimlaneData.tasksPaginated.endCursor
          pagination.hasMore = swimlaneData.tasksPaginated.hasNextPage
          pagination.totalCount = swimlaneData.tasksPaginated.totalCount
        }
      }
    }
  } catch (err) {
    console.error('Failed to load more swimlane tasks:', err)
  } finally {
    pagination.loading = false
  }
}

// Helper function to build board URL with current session parameters
function buildBoardUrlWithSessionParams() {
  if (!props.boardId) return null

  let url
  // Try to determine board type from board.value, or fallback to props
  if (board.value?.isProjectBoard && props.projectId) {
    url = `/projects/${props.projectId}/boards/${props.boardId}`
  } else if (board.value?.isPersonalBoard) {
    url = `/boards/${props.boardId}`
  } else if (props.projectId) {
    // Fallback: if we have projectId, assume it's a project board
    url = `/projects/${props.projectId}/boards/${props.boardId}`
  } else {
    // Fallback: assume personal board if no projectId
    url = `/boards/${props.boardId}`
  }

  const params = []

  // Add session filters if set
  if (sessionFilters.value !== null && sessionFilters.value !== undefined) {
    if (
      typeof sessionFilters.value === 'object' &&
      Object.keys(sessionFilters.value).length === 0
    ) {
      // Empty object = user explicitly wants no filters
      params.push('sessionFilters={}')
    } else if (
      typeof sessionFilters.value === 'object' &&
      Object.keys(sessionFilters.value).length > 0
    ) {
      // Non-empty object = user set specific filters
      params.push(`sessionFilters=${encodeURIComponent(JSON.stringify(sessionFilters.value))}`)
    }
  }

  // Add session ordering if different from board defaults
  if (
    sessionOrderBy.value &&
    sessionOrderBy.value !== '' &&
    sessionOrderBy.value !== boardDefaultOrderBy.value
  ) {
    params.push(`sessionOrderBy=${encodeURIComponent(sessionOrderBy.value)}`)
  }
  if (
    sessionOrderDirection.value &&
    sessionOrderDirection.value !== '' &&
    sessionOrderDirection.value !== boardDefaultOrderDirection.value
  ) {
    params.push(`sessionOrderDirection=${encodeURIComponent(sessionOrderDirection.value)}`)
  }

  // Add session swimlane mode if different from board default
  if (sessionSwimlaneMode.value && sessionSwimlaneMode.value !== '') {
    const boardSwimlaneMode = board.value?.swimlaneMode || 'none'
    if (sessionSwimlaneMode.value !== boardSwimlaneMode) {
      params.push(`sessionSwimlaneMode=${encodeURIComponent(sessionSwimlaneMode.value)}`)
    }
  }

  // Add chat_id and from_id
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)

  if (params.length > 0) url += '?' + params.join('&')
  return url
}

function triggerDragReadyFeedback() {
  if (typeof window === 'undefined') return
  try {
    if (window.Telegram?.WebApp?.HapticFeedback?.impactOccurred) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
      return
    }
  } catch (_e) {
    // Fall through to navigator vibration
  }

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(12)
  }
}

function goToTask(task, event) {
  // Don't navigate if a drag operation is in progress
  if (isDragging.value) {
    return
  }
  if (Date.now() - lastDragEndedAt.value < 220) {
    return
  }
  if (event?.target?.closest?.('.task-drag-handle')) {
    return
  }
  if (event?.target?.closest?.('.task-transition-handle')) {
    return
  }
  if (event?.defaultPrevented) {
    return
  }

  const projectId = task.project?.id || props.projectId
  if (!projectId) return

  // Store board URL with session parameters before navigating
  const boardUrlWithParams = buildBoardUrlWithSessionParams()
  if (boardUrlWithParams) {
    try {
      sessionStorage.setItem(`boardUrl_${props.boardId}`, boardUrlWithParams)
      // Clear conflicting URLs to prevent stale data
      sessionStorage.removeItem('dashboardUrl')
      sessionStorage.removeItem('calendarUrl')
      // Note: We can't clear all project task URLs, but referrer check will handle priority
    } catch (e) {
      console.warn('Failed to store board URL:', e)
    }
  }

  const taskId = task.iid
  if (!taskId) return
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''
  const boardParam = props.boardId
    ? `${chat || from ? '&' : '?'}board_id=${encodeURIComponent(props.boardId)}`
    : ''
  window.location.href = `/projects/${projectId}/tasks/${taskId}${chat}${from}${boardParam}`
}

// Drag-and-drop transition handlers
async function handleTaskDrop(event) {
  // Prevent default behavior
  if (event.item) {
    event.item.style.opacity = '' // Restore opacity
  }

  const taskId = event.item?.dataset?.taskId
  if (!taskId) {
    return
  }

  // Find source and target columns
  const sourceColumnElement = event.from.closest('[data-column-id]')
  const targetColumnElement = event.to.closest('[data-column-id]')

  if (!sourceColumnElement || !targetColumnElement) {
    // Revert the drag if we can't find columns
    if (event.from && event.item && event.oldIndex !== null) {
      const children = Array.from(event.from.children)
      const insertBefore = children[event.oldIndex] || null
      event.from.insertBefore(event.item, insertBefore)
    }
    return
  }

  const sourceColumnId = sourceColumnElement.dataset.columnId
  const targetColumnId = targetColumnElement.dataset.columnId

  if (!sourceColumnId || !targetColumnId || sourceColumnId === targetColumnId) {
    // Revert the drag if invalid
    if (event.from && event.item && event.oldIndex !== null) {
      const children = Array.from(event.from.children)
      const insertBefore = children[event.oldIndex] || null
      event.from.insertBefore(event.item, insertBefore)
    }
    return
  }

  // Check swimlane restrictions
  const swimlaneMode = getEffectiveSwimlaneMode()
  if (swimlaneMode !== 'none') {
    const sourceSwimlane = event.from.closest('.swimlane-row')
    const targetSwimlane = event.to.closest('.swimlane-row')
    if (sourceSwimlane !== targetSwimlane) {
      // Revert drag - different swimlanes
      // Sortable will revert automatically, but we need to prevent the drop
      if (toastManagerRef.value) {
        toastManagerRef.value.error(s__('Boards|Cannot drag tasks between different swimlanes'))
      }
      // Move item back to original position
      event.from.insertBefore(
        event.item,
        event.oldIndex !== null ? event.from.children[event.oldIndex] : null
      )
      return
    }
  }

  // Find task in current data
  const task = findTaskInBoard(taskId)
  if (!task) {
    // Move item back
    event.from.insertBefore(
      event.item,
      event.oldIndex !== null ? event.from.children[event.oldIndex] : null
    )
    return
  }

  // Get target column workflow statuses
  const targetColumn = board.value?.boardColumns?.find(col => col.id === targetColumnId)
  if (
    !targetColumn ||
    !targetColumn.workflowStatuses ||
    targetColumn.workflowStatuses.length === 0
  ) {
    // Revert drag - no statuses in target column
    event.from.insertBefore(
      event.item,
      event.oldIndex !== null ? event.from.children[event.oldIndex] : null
    )
    if (toastManagerRef.value) {
      toastManagerRef.value.warning(s__('Boards|Target column has no workflow statuses'))
    }
    return
  }

  // Load available transitions for the task filtered by target column
  try {
    const projectId = task.project?.id || props.projectId
    if (!projectId) {
      throw new Error('Project ID is required')
    }

    // Update variables and load query with board_column_id to filter on backend
    taskTransitionsVars.value = {
      projectId: String(projectId),
      taskId: String(taskId),
      boardColumnId: targetColumnId ? String(targetColumnId) : null,
    }

    // Create promise to wait for result callback
    const transitionsResultPromise = new Promise(resolve => {
      transitionsResultResolver = resolve
    })

    await loadTaskTransitions()

    // Wait for query to complete - use callback if available quickly, otherwise wait for loading
    let resultData = null

    // Try to get result from callback quickly (500ms timeout)
    try {
      resultData = await Promise.race([
        transitionsResultPromise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 500)),
      ])
    } catch (err) {
      // Wait for loading to finish (with shorter intervals)
      let retries = 0
      while (loadingTaskTransitions.value && retries < 20) {
        await new Promise(resolve => setTimeout(resolve, 50))
        retries++
        // Check if callback fired while waiting
        if (rawTaskTransitionsResult.value) {
          resultData = rawTaskTransitionsResult.value
          break
        }
      }
      await nextTick()
    }

    await nextTick()

    // Check if query completed successfully
    if (taskTransitionsResult.value?.error) {
      console.error('Task transitions query error:', taskTransitionsResult.value.error)
      event.from.insertBefore(
        event.item,
        event.oldIndex !== null ? event.from.children[event.oldIndex] : null
      )
      if (toastManagerRef.value) {
        toastManagerRef.value.error(s__('Boards|Failed to load available transitions'))
      }
      return
    }

    // Backend already filtered transitions to only those leading to target column statuses
    // Use result from callback (before cache normalization) if available
    let validTransitions = []

    // Try callback result first (deep cloned, not affected by cache)
    if (resultData?.project?.task?.availableTransitions) {
      const rawTransitions = resultData.project.task.availableTransitions
      if (Array.isArray(rawTransitions) && rawTransitions.length > 0) {
        validTransitions = rawTransitions
      }
    }

    // Fall back to rawTaskTransitionsResult if callback didn't work
    if (
      validTransitions.length === 0 &&
      rawTaskTransitionsResult.value?.project?.task?.availableTransitions
    ) {
      const rawTransitions = rawTaskTransitionsResult.value.project.task.availableTransitions
      if (Array.isArray(rawTransitions) && rawTransitions.length > 0) {
        validTransitions = rawTransitions
      }
    }

    // Fall back to normalized result if both above are empty
    if (
      validTransitions.length === 0 &&
      taskTransitionsResult.value?.project?.task?.availableTransitions
    ) {
      const normalizedTransitions = taskTransitionsResult.value.project.task.availableTransitions
      if (Array.isArray(normalizedTransitions) && normalizedTransitions.length > 0) {
        validTransitions = normalizedTransitions
      }
    }

    if (validTransitions.length === 0) {
      // Revert drag - no valid transitions
      event.from.insertBefore(
        event.item,
        event.oldIndex !== null ? event.from.children[event.oldIndex] : null
      )
      if (toastManagerRef.value) {
        toastManagerRef.value.warning(s__('Boards|No valid transitions available to target column'))
      }
      return
    }

    // Store pending transition info including drag event for reverting
    pendingTransition.value = {
      task,
      taskId,
      sourceColumnId,
      targetColumnId,
      transitions: validTransitions,
      targetStatus: validTransitions[0].targetStatus,
      dragEvent: event, // Store event to revert drag if canceled
    }

    // Show appropriate modal
    if (validTransitions.length === 1) {
      showTransitionConfirmation.value = true
    } else {
      showTransitionSelection.value = true
    }
  } catch (error) {
    console.error('Failed to load transitions:', error)
    event.from.insertBefore(
      event.item,
      event.oldIndex !== null ? event.from.children[event.oldIndex] : null
    )
    if (toastManagerRef.value) {
      toastManagerRef.value.error(s__('Boards|Failed to load available transitions'))
    }
  }
}

async function openTransitionSelectionFromMobile(task, sourceColumnId = null) {
  if (!task?.id || !task?.iid || task.archived || !task.workflowStatus) return
  if (executingTransition.value || loadingTaskTransitions.value) return

  const projectId = task.project?.id || props.projectId || board.value?.project?.id
  if (!projectId) {
    if (toastManagerRef.value) {
      toastManagerRef.value.error(s__('Boards|Project not found for this task'))
    }
    return
  }

  activeTransitionTaskId.value = String(task.id)

  try {
    rawTaskTransitionsResult.value = null
    transitionsResultResolver = null
    taskTransitionsVars.value = {
      projectId: String(projectId),
      taskId: String(task.id),
      boardColumnId: null,
    }

    await loadTaskTransitions()

    let retries = 0
    while (loadingTaskTransitions.value && retries < 40) {
      await nextTick()
      await new Promise(resolve => requestAnimationFrame(resolve))
      retries++
    }

    await nextTick()
    const resultData = rawTaskTransitionsResult.value || taskTransitionsResult.value

    if (taskTransitionsResult.value?.error) {
      console.error('Task transitions query error:', taskTransitionsResult.value.error)
      if (toastManagerRef.value) {
        toastManagerRef.value.error(s__('Boards|Failed to load available transitions'))
      }
      return
    }

    let validTransitions = []
    if (resultData?.project?.task?.availableTransitions) {
      const rawTransitions = resultData.project.task.availableTransitions
      if (Array.isArray(rawTransitions) && rawTransitions.length > 0) {
        validTransitions = rawTransitions
      }
    }

    if (
      validTransitions.length === 0 &&
      rawTaskTransitionsResult.value?.project?.task?.availableTransitions
    ) {
      const rawTransitions = rawTaskTransitionsResult.value.project.task.availableTransitions
      if (Array.isArray(rawTransitions) && rawTransitions.length > 0) {
        validTransitions = rawTransitions
      }
    }

    if (
      validTransitions.length === 0 &&
      taskTransitionsResult.value?.project?.task?.availableTransitions
    ) {
      const normalizedTransitions = taskTransitionsResult.value.project.task.availableTransitions
      if (Array.isArray(normalizedTransitions) && normalizedTransitions.length > 0) {
        validTransitions = normalizedTransitions
      }
    }

    if (validTransitions.length === 0) {
      if (toastManagerRef.value) {
        toastManagerRef.value.warning(s__('Boards|No valid transitions available'))
      }
      return
    }

    pendingTransition.value = {
      task,
      taskId: String(task.id),
      sourceColumnId: sourceColumnId ? String(sourceColumnId) : null,
      targetColumnId: null,
      transitions: validTransitions,
      targetStatus: validTransitions[0]?.targetStatus || null,
      dragEvent: null,
    }

    showTransitionSelection.value = true
  } catch (error) {
    console.error('Failed to load transitions:', error)
    if (toastManagerRef.value) {
      toastManagerRef.value.error(s__('Boards|Failed to load available transitions'))
    }
  } finally {
    activeTransitionTaskId.value = null
  }
}

function findTaskInBoard(taskId) {
  const searchTaskId = String(taskId) // Normalize to string for comparison

  // Search in regular columns (when not in swimlane mode)
  const swimlaneMode = getEffectiveSwimlaneMode()
  if (swimlaneMode === 'none') {
    for (const column of board.value?.boardColumns || []) {
      const tasks = columnTasks.value[column.id] || []
      const task = tasks.find(t => String(t.id) === searchTaskId)
      if (task) return task
    }
  } else {
    // Search in aligned swimlanes (swimlane mode)
    for (const alignedSwimlane of alignedSwimlanes.value) {
      for (const column of board.value?.boardColumns || []) {
        const swimlaneTasks = alignedSwimlane.tasksByColumn?.[column.id] || []
        const task = swimlaneTasks.find(t => String(t.id) === searchTaskId)
        if (task) return task
      }
    }
  }

  return null
}

async function confirmTransition(transition) {
  if (!pendingTransition.value || executingTransition.value) return

  executingTransition.value = true
  try {
    const result = await executeTransitionMutation({
      taskId: pendingTransition.value.taskId,
      transitionId: transition.id,
    })

    if (result?.data?.executeTransition?.errors?.length > 0) {
      // Reset dragging flag immediately to prevent blocking further drags
      isDragging.value = false

      // Revert the drag visually before refetching
      if (pendingTransition.value?.dragEvent) {
        const event = pendingTransition.value.dragEvent
        if (event.from && event.item && event.oldIndex !== null) {
          const children = Array.from(event.from.children)
          const insertBefore = children[event.oldIndex] || null
          event.from.insertBefore(event.item, insertBefore)
        }
      }

      if (toastManagerRef.value) {
        const errorMessages = result.data.executeTransition.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        toastManagerRef.value.error(errorMessages)
      }

      // Close modals before refetching
      showTransitionConfirmation.value = false
      showTransitionSelection.value = false
      const savedDragEvent = pendingTransition.value?.dragEvent
      pendingTransition.value = null

      // Revert the drag by refetching board
      await refetchBoard()

      // Reinitialize Sortable after refetch to restore drag-and-drop functionality
      await nextTick()
      initializeSortable()
    } else {
      if (toastManagerRef.value) {
        toastManagerRef.value.success(s__('Boards|Transition executed successfully'))
      }
      // Refetch board to update task positions
      await refetchBoard()
    }
  } catch (error) {
    console.error('Failed to execute transition:', error)

    // Reset dragging flag immediately to prevent blocking further drags
    isDragging.value = false

    // Revert the drag visually before refetching
    if (pendingTransition.value?.dragEvent) {
      const event = pendingTransition.value.dragEvent
      if (event.from && event.item && event.oldIndex !== null) {
        const children = Array.from(event.from.children)
        const insertBefore = children[event.oldIndex] || null
        event.from.insertBefore(event.item, insertBefore)
      }
    }

    if (toastManagerRef.value) {
      toastManagerRef.value.error(s__('Boards|Failed to execute transition'))
    }

    // Close modals before refetching
    showTransitionConfirmation.value = false
    showTransitionSelection.value = false
    pendingTransition.value = null

    // Revert the drag by refetching board
    await refetchBoard()

    // Reinitialize Sortable after refetch to restore drag-and-drop functionality
    await nextTick()
    initializeSortable()
  } finally {
    executingTransition.value = false
    // Only close modals if they weren't already closed in error handling
    if (showTransitionConfirmation.value || showTransitionSelection.value) {
      showTransitionConfirmation.value = false
      showTransitionSelection.value = false
    }
    // Only clear pendingTransition if it wasn't already cleared
    if (pendingTransition.value) {
      pendingTransition.value = null
    }
  }
}

async function cancelTransition() {
  // Reset dragging flag immediately to prevent blocking further drags
  isDragging.value = false

  // Revert the drag visually before closing modals
  if (pendingTransition.value?.dragEvent) {
    const event = pendingTransition.value.dragEvent
    // Move item back to original position
    if (event.from && event.item && event.oldIndex !== null) {
      const children = Array.from(event.from.children)
      const insertBefore = children[event.oldIndex] || null
      event.from.insertBefore(event.item, insertBefore)
    }
  }

  showTransitionConfirmation.value = false
  showTransitionSelection.value = false
  pendingTransition.value = null

  // Refetch board to ensure state is consistent
  await refetchBoard()

  // Ensure SortableJS is reinitialized after refetch
  await nextTick()
  initializeSortable()
}

function initializeSortable() {
  // Clean up existing instances
  sortableInstances.value.forEach(instance => {
    try {
      instance.destroy()
    } catch (e) {
      // Ignore errors during cleanup
    }
  })
  sortableInstances.value = []

  const swimlaneMode = getEffectiveSwimlaneMode()

  if (swimlaneMode !== 'none') {
    // Initialize Sortable for each swimlane row
    // Each swimlane should only allow drag-and-drop within its own row
    const swimlaneRows = document.querySelectorAll('.swimlane-row')

    swimlaneRows.forEach(swimlaneRow => {
      const swimlaneKey = swimlaneRow.dataset.swimlaneKey || 'default'
      const columnCells = swimlaneRow.querySelectorAll('.swimlane-column-cell')

      columnCells.forEach(cell => {
        const list = cell.querySelector('ul[data-sortable-list]')
        const listId = list?.dataset?.sortableList || 'unknown'

        // Check if list exists, is in DOM, and doesn't already have SortableJS attached
        if (list && list.parentElement && document.body.contains(list)) {
          // Check if SortableJS is already attached to this element
          if (list.sortable) {
            return
          }
          try {
            const sortable = Sortable.create(list, {
              group: {
                name: `swimlane-${swimlaneKey}`,
                pull: true, // Allow pulling within the same group (same swimlane)
                put: to => {
                  // Only allow dropping within the same swimlane
                  if (!to || !to.el) return false
                  // to.el is the target list (ul element)
                  // Find the swimlane row that contains this list
                  const targetSwimlaneRow = to.el.closest('.swimlane-row')
                  // Allow drop only if target is in the same swimlane row
                  const isSameSwimlane = targetSwimlaneRow === swimlaneRow
                  return isSameSwimlane
                },
              },
              animation: 150,
              handle: '.task-drag-handle',
              draggable: '.board-task-card:not(.sortable-placeholder)',
              filter: '.sortable-placeholder',
              delayOnTouchOnly: true,
              delay: 180,
              touchStartThreshold: 8,
              fallbackTolerance: 8,
              ghostClass: 'drag-ghost',
              chosenClass: 'drag-chosen',
              dragClass: 'drag-active',
              onStart: evt => {
                isDragging.value = true
                triggerDragReadyFeedback()
              },
              onEnd: evt => {
                handleTaskDrop(evt)
                lastDragEndedAt.value = Date.now()
                isDragging.value = false
              },
              emptyInsertThreshold: 5,
              swapThreshold: 0.65,
            })
            sortableInstances.value.push(sortable)
          } catch (e) {
            console.error('Failed to initialize Sortable for swimlane column:', e)
          }
        }
      })
    })
  } else {
    // Initialize Sortable for regular columns
    // All columns can accept tasks from any other column
    // Use a more specific selector to find all lists directly
    const lists = document.querySelectorAll('.board-column ul[data-sortable-list]')

    lists.forEach(list => {
      const listId = list?.dataset?.sortableList || 'unknown'
      const column = list.closest('.board-column')
      const columnId = column?.dataset?.columnId || 'unknown'

      // Check if list exists, is in DOM, and doesn't already have SortableJS attached
      if (list && list.parentElement && document.body.contains(list)) {
        // Check if SortableJS is already attached to this element
        if (list.sortable) {
          return
        }
        try {
          const sortable = Sortable.create(list, {
            group: 'board-columns',
            animation: 150,
            handle: '.task-drag-handle',
            draggable: '.board-task-card:not(.sortable-placeholder)',
            filter: '.sortable-placeholder',
            delayOnTouchOnly: true,
            delay: 180,
            touchStartThreshold: 8,
            fallbackTolerance: 8,
            ghostClass: 'drag-ghost',
            chosenClass: 'drag-chosen',
            dragClass: 'drag-active',
            onStart: evt => {
              isDragging.value = true
              triggerDragReadyFeedback()
            },
            onEnd: evt => {
              handleTaskDrop(evt)
              lastDragEndedAt.value = Date.now()
              isDragging.value = false
            },
            emptyInsertThreshold: 5,
            swapThreshold: 0.65,
          })
          sortableInstances.value.push(sortable)
        } catch (e) {
          console.error('Failed to initialize Sortable for column:', e)
        }
      }
    })
  }
}

// Watch for board content changes to reinitialize Sortable
watch(
  () => [board.value?.boardColumns, alignedSwimlanes.value, columnTasks.value],
  () => {
    nextTick(() => {
      if (board.value && board.value.boardColumns && board.value.boardColumns.length > 0) {
        // Wait for DOM to be fully updated, especially after pagination loads
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          setTimeout(() => {
            initializeSortable()
          }, 100) // Increased delay to ensure all DOM updates are complete
        })
      }
    })
  },
  { deep: true }
)
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.error {
  color: #dc3545;
  font-size: 13px;
}

.board-filters-wrapper {
  margin-bottom: 16px;
}

.board-quick-filter-group {
  margin-bottom: 12px;
}

.board-quick-filter-options {
  font-size: 13px;
}

@media (min-width: 768px) {
  .board-quick-filter-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .board-quick-filter-option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.board-quick-filter-option input {
  margin: 0;
}

.board-quick-filter-hint {
  font-size: 13px;
  color: #555;
  margin-bottom: 12px;
}

.board-inline-actions.board-inline-actions-settings {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.board-advanced-controls-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 10px 0 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

:deep(.board-advanced-controls-row .ordering-wrapper),
:deep(.board-advanced-controls-row .single-select-setting) {
  margin-bottom: 0;
}

@media (min-width: 1024px) {
  .board-advanced-controls-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.expand-icon {
  font-size: 12px;
  color: #666;
}

.active-count {
  color: #1d6bf3;
  font-weight: 700;
}
.board-container {
  -webkit-overflow-scrolling: touch;
}

.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
  overflow: visible;
  /* Add 24px padding to container */
  padding: 24px !important;
}

.card.full-width .board-container {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* Remove padding from board-container since parent has padding */
  padding-left: 0;
  padding-right: 0;
  margin-left: 0;
  margin-right: 0;
}

/* Content wrappers */
.card.full-width .board-swimlanes-wrapper {
  margin-left: 0;
}

.card.full-width .board-columns {
  margin-left: 0;
}

/* Sticky header should stick to container's padded edge */
.card.full-width .swimlane-header {
  left: 24px !important; /* Match container padding-left */
}

/* Content wrappers - no extra padding needed, card padding handles it */
.card.full-width .board-swimlanes-wrapper {
  padding-left: 0;
  padding-right: 0;
}

.card.full-width .board-columns {
  padding-left: 0;
  padding-right: 0;
}

/* Sticky header sticks to left edge of scrollable container */
/* In full-width mode, card has 24px padding, so header is 24px from screen edge */
.card.full-width .swimlane-header {
  left: 0 !important;
}

.card.full-width .board-swimlanes-wrapper,
.card.full-width .board-columns {
  width: fit-content;
  margin: 0 auto;
}

.board-columns {
  padding-bottom: 16px;
}

.board-swimlanes-wrapper {
  width: max-content;
  min-width: max-content;
}

.swimlane-row {
  width: max-content;
  min-width: max-content;
}

.board-task-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.task-card-header .task-title {
  flex: 1 1 auto;
  min-width: 0;
}

.task-drag-handle {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #888;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  user-select: none;
  touch-action: none;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
}

.task-transition-handle {
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  flex: 0 0 32px;
  padding: 0;
  gap: 0;
  line-height: 1;
  font-size: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #666;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
}

.task-transition-handle:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #444;
}

.task-transition-handle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.task-transition-handle :deep(.transition-icon) {
  flex-shrink: 0;
  color: #0088cc;
}

.loading-dots {
  letter-spacing: 1px;
}

.task-drag-handle:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #666;
}

.task-drag-handle:active {
  cursor: grabbing;
}

.drag-chosen {
  box-shadow: 0 0 0 2px rgba(42, 171, 238, 0.25);
}

.drag-ghost {
  opacity: 0.75;
}

.drag-active {
  cursor: grabbing !important;
}

.board-task-card.archived-task {
  opacity: 0.6;
  background-color: #f8f9fa;
}

.task-title {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.task-meta-item {
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-meta {
  display: grid;
  gap: 2px;
}

.task-meta-project-role {
  font-size: 0.9em;
  color: #9e9e9e;
  font-style: italic;
  margin-left: 4px;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archived-label {
  background-color: #6c757d;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
}

.personal-label-badge {
  border: 1px dashed rgba(255, 255, 255, 0.5);
}

/* Warning message styles (similar to confidential task message) */
.task-info-container {
  padding: 16px;
}

.confidential-message {
  text-align: center;
  padding: 48px 24px;
  max-width: 600px;
  margin: 0 auto;
}

.confidential-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.confidential-message h3 {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 12px 0;
}

.confidential-message p {
  font-size: 14px;
  color: #757575;
  line-height: 1.6;
  margin: 0;
}

/* Match color input height and width with other inputs */
input[type='color'] {
  width: 100%;
  height: 40px;
  min-height: 40px;
  padding: 4px;
  cursor: pointer;
  border: 1px solid #d0d7de;
  border-radius: 8px;
}

@media (max-width: 767px) {
  input[type='color'] {
    height: 44px;
    min-height: 44px;
  }
}

/* Button styles inherit from forms.css */

.btn-icon {
  display: none;
}

.board-header-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-height: 32px;
  padding: 6px 10px;
  gap: 6px;
  line-height: 1;
}

.board-header-action-btn .btn-text {
  line-height: 1;
}

.board-header-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.board-header-action-icon :deep(svg) {
  width: 14px;
  height: 14px;
}

/* Mobile responsive styles - matching projects/tasks/Index.vue */
@media (max-width: 640px) {
  .btn-text {
    display: none;
  }

  .btn-icon {
    display: inline;
  }

  .btn-link-secondary {
    min-width: 36px;
    padding: 8px;
    text-align: center;
  }
}

@media (max-width: 768px) {
  .board-inline-actions.board-inline-actions-settings {
    width: 100%;
    justify-content: stretch;
  }

  .board-inline-actions.board-inline-actions-settings > .btn-sm {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    text-align: center;
    justify-content: center;
  }

  .board-task-card {
    padding: 10px !important;
    margin-bottom: 10px !important;
  }

  .task-title {
    font-size: 13px !important;
    margin-bottom: 4px !important;
    line-height: 1.35;
  }

  .task-labels {
    margin-bottom: 4px !important;
  }

  .task-meta {
    font-size: 10px !important;
  }

  /* Reduce mobile density: keep due/priority/status, hide secondary metadata */
  .task-meta-assignee,
  .task-meta-author,
  .task-meta-project,
  .task-meta-finished {
    display: none;
  }

  .task-drag-handle {
    width: 34px;
    height: 34px;
    min-width: 34px;
  }

  .task-transition-handle {
    width: 34px;
    height: 34px;
    min-height: 34px;
    min-width: 34px;
    flex-basis: 34px;
  }

  .sortable-placeholder {
    min-height: 56px;
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Hide full-width toggle button on mobile and tablet, show on laptop and larger */
.btn-full-width-toggle {
  display: none;
}

@media (min-width: 768px) {
  .btn-full-width-toggle {
    display: inline-flex;
  }
}

/* Column action buttons - show icons on xs screens, text on larger screens */
.column-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.column-action-btn .btn-text {
  display: inline;
}

.column-action-btn > span:not(.btn-text) {
  display: none;
}

@media (max-width: 640px) {
  .column-action-btn .btn-text {
    display: none;
  }

  .column-action-btn > span:not(.btn-text) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .column-action-btn {
    min-width: 32px;
    padding: 4px;
    justify-content: center;
  }
}

.info-icon-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.info-icon-button:hover {
  background-color: #f0f0f0;
}

.board-mobile-menu {
  position: relative;
}

.board-mobile-actions-line {
  margin-top: -6px;
  margin-bottom: 12px;
}

.board-mobile-actions-row {
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 8px;
  width: 100%;
}

.board-mobile-menu-button {
  min-width: 0;
  flex: 1 1 50%;
  text-align: center;
}

.board-mobile-menu-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 200px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}

.board-mobile-menu-item {
  width: 100%;
  display: block;
  text-align: left;
  background: #fff;
  border: none;
  color: #333;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
}

.board-mobile-menu-item:hover {
  background: #f5f5f5;
}

.board-mobile-controls-trigger {
  min-width: 0;
  flex: 1 1 50%;
  text-align: center;
}

.board-controls-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 920;
}

.board-mobile-controls-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
}

.board-mobile-controls-close {
  border: none;
  background: transparent;
  color: #666;
  font-size: 22px;
  line-height: 1;
  padding: 2px 6px;
  cursor: pointer;
}

.board-mobile-column-pager {
  margin-top: 12px;
  margin-bottom: 12px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}

.board-mobile-column-pager::-webkit-scrollbar {
  display: none;
}

.board-mobile-column-chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #333;
  font-size: 12px;
  padding: 6px 10px;
}

.board-mobile-column-chip.active {
  background: #e6f2ff;
  border-color: #2aabee;
  color: #0f4c75;
}

.board-mobile-column-chip:hover:not(:disabled) {
  background: #fff;
}

.board-mobile-column-chip-title {
  max-width: 110px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.board-mobile-column-chip-count {
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #555;
  font-size: 11px;
  padding: 0 5px;
}

@media (max-width: 768px) {
  .board-header-row {
    justify-content: center !important;
  }

  .board-header-info {
    text-align: center;
  }

  .board-header-title {
    justify-content: center;
  }

  .board-controls-container.mobile-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 930;
    background: #fff;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 20px rgba(0, 0, 0, 0.16);
    max-height: 85vh;
    overflow-y: auto;
    padding: 16px 16px 28px;
    transform: translateY(100%);
    transition: transform 0.24s ease;
    pointer-events: none;
  }

  .board-controls-container.mobile-sheet.open {
    transform: translateY(0);
    pointer-events: auto;
  }
}
</style>
